import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Lock, Save, Plus, Edit, Trash2, X, Settings, Users, Briefcase, 
  FolderOpen, Wrench, Home, Eye, EyeOff, GripVertical, Image,
  Globe, FileText, Layers, ChevronDown, ChevronUp, LayoutDashboard,
  Upload, RotateCcw, RotateCw, Undo2, Redo2, RefreshCw, ImageIcon,
  Search, Filter, Check, AlertTriangle, FolderPlus, Tag, Replace,
  Building2, Mail, Phone, MapPin, Award, Link, Calendar, Clock,
  Send, TestTube, History, Shield, ExternalLink, TrendingUp, Edit3,
  Newspaper, Rss, Pin, EyeIcon, EyeOffIcon, CheckCircle2, Target, Heart,
  Info, AlertCircle, UserPlus, LogOut, KeyRound, ShieldCheck, Database,
  BarChart2, Activity
} from 'lucide-react';
import SEO from '../components/SEO';
import { ImageLibraryManager } from '../components/ImageLibraryManager';
import { useAuth, UserRoles } from '../contexts/AuthContext';
import CMSLogin from '../components/CMSLogin';
import UserManagement from '../components/UserManagement';
import TwoFactorSetup from '../components/TwoFactorSetup';
import DataMigration from '../components/DataMigration';
import SEOManager from '../components/SEOManager';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { toast } = useToast();
  const { user, loading: authLoading, logout, isAdmin, canEdit, getToken } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  
  // Data states
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [jobListings, setJobListings] = useState([]);
  const [pages, setPages] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [trashItems, setTrashItems] = useState([]);
  const [uploadedImages, setUploadedImages] = useState([]);
  
  // New CMS Data states
  const [companyInfo, setCompanyInfo] = useState({});
  const [contactSettings, setContactSettings] = useState({});
  const [accreditations, setAccreditations] = useState([]);
  const [versionHistory, setVersionHistory] = useState([]);
  const [careerLevels, setCareerLevels] = useState([]);
  const [careerProgressionSettings, setCareerProgressionSettings] = useState({});
  
  // Image Library states
  const [imageLibrary, setImageLibrary] = useState([]);
  const [imageFolders, setImageFolders] = useState([]);
  const [folderCounts, setFolderCounts] = useState({});
  const [selectedFolder, setSelectedFolder] = useState('All');
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [showImageLibraryModal, setShowImageLibraryModal] = useState(false);
  const [imageSelectCallback, setImageSelectCallback] = useState(null);
  const [selectedImageForDetails, setSelectedImageForDetails] = useState(null);
  const [uploadingToLibrary, setUploadingToLibrary] = useState(false);
  const [imagePagination, setImagePagination] = useState({ total: 0, skip: 0, limit: 50, hasMore: false });
  const [loadingMoreImages, setLoadingMoreImages] = useState(false);
  
  // Media Health states
  const [mediaHealth, setMediaHealth] = useState(null);
  const [mediaHealthLoading, setMediaHealthLoading] = useState(false);
  
  // Editing states
  const [editingItem, setEditingItem] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newFormType, setNewFormType] = useState(null);
  
  // Publishing states
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingStatus, setSavingStatus] = useState('saved'); // 'saved', 'saving', 'unsaved'
  
  // Undo/Redo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Save state to history for undo/redo
  const saveToHistory = useCallback((action, data) => {
    const newEntry = { action, data, timestamp: new Date().toISOString() };
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newEntry);
    if (newHistory.length > 20) newHistory.shift(); // Keep last 20 actions
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setCanUndo(true);
    setCanRedo(false);
  }, [history, historyIndex]);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [servicesRes, projectsRes, sectorsRes, teamRes, jobsRes, settingsRes, pagesRes, trashRes, uploadsRes, companyRes, contactRes, accredRes, careerLevelsRes, careerSettingsRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/projects`),
        axios.get(`${API}/sectors`),
        axios.get(`${API}/team`),
        axios.get(`${API}/jobs`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/pages`),
        axios.get(`${API}/trash`).catch(() => ({ data: { trash: [] } })),
        axios.get(`${API}/uploads`).catch(() => ({ data: { uploads: [] } })),
        axios.get(`${API}/company-info`).catch(() => ({ data: { companyInfo: {} } })),
        axios.get(`${API}/contact-settings`).catch(() => ({ data: { contactSettings: {} } })),
        axios.get(`${API}/accreditations`).catch(() => ({ data: { accreditations: [] } })),
        axios.get(`${API}/career-levels`).catch(() => ({ data: { levels: [] } })),
        axios.get(`${API}/career-progression-settings`).catch(() => ({ data: { settings: {} } }))
      ]);
      setServices(servicesRes.data.services || []);
      setProjects(projectsRes.data.projects || []);
      setSectors(sectorsRes.data.sectors || []);
      setTeamMembers(teamRes.data.team || []);
      setJobListings(jobsRes.data.jobs || []);
      setSiteSettings(settingsRes.data.settings || {});
      setPages(pagesRes.data.pages || []);
      setTrashItems(trashRes.data.trash || []);
      setUploadedImages(uploadsRes.data.uploads || []);
      setCompanyInfo(companyRes.data.companyInfo || {});
      setContactSettings(contactRes.data.contactSettings || {});
      setAccreditations(accredRes.data.accreditations || []);
      setCareerLevels(careerLevelsRes.data.levels || []);
      setCareerProgressionSettings(careerSettingsRes.data.settings || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: "Error", description: "Failed to load data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Fetch Image Library data with pagination
  const fetchImageLibrary = useCallback(async (folder = 'All', search = '', append = false, skip = 0) => {
    try {
      const params = new URLSearchParams();
      if (folder && folder !== 'All') params.append('folder', folder);
      if (search) params.append('search', search);
      params.append('skip', skip.toString());
      params.append('limit', '50');
      
      const response = await axios.get(`${API}/images?${params.toString()}`);
      const newImages = response.data.images || [];
      
      if (append) {
        setImageLibrary(prev => [...prev, ...newImages]);
      } else {
        setImageLibrary(newImages);
      }
      
      setImageFolders(response.data.folders || ['All', 'Homepage', 'Services', 'Projects', 'Sectors', 'Team', 'Logos', 'Other']);
      setFolderCounts(response.data.folderCounts || {});
      setImagePagination(response.data.pagination || { total: 0, skip: 0, limit: 50, hasMore: false });
    } catch (error) {
      console.error('Error fetching image library:', error);
    }
  }, []);
  
  // Load more images for pagination
  const loadMoreImages = useCallback(async () => {
    if (loadingMoreImages || !imagePagination.hasMore) return;
    
    setLoadingMoreImages(true);
    try {
      await fetchImageLibrary(selectedFolder, imageSearchQuery, true, imagePagination.skip + imagePagination.limit);
    } finally {
      setLoadingMoreImages(false);
    }
  }, [fetchImageLibrary, loadingMoreImages, imagePagination, selectedFolder, imageSearchQuery]);

  // Upload image to library
  const uploadToLibrary = async (file, folder = 'Other') => {
    console.log('[uploadToLibrary] Starting upload:', { name: file?.name, type: file?.type, size: file?.size, folder });
    if (!file) {
      console.log('[uploadToLibrary] No file provided');
      return null;
    }
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      console.log('[uploadToLibrary] Invalid file type:', file.type);
      toast({ title: "Error", description: "Please upload JPG, PNG, WebP, SVG, or GIF", variant: "destructive" });
      return null;
    }

    if (file.size > 10 * 1024 * 1024) {
      console.log('[uploadToLibrary] File too large:', file.size);
      toast({ title: "Error", description: "File size must be under 10MB", variant: "destructive" });
      return null;
    }

    setUploadingToLibrary(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    try {
      console.log('[uploadToLibrary] Sending request to API...');
      const response = await axios.post(`${API}/images/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      console.log('[uploadToLibrary] Upload successful:', response.data.image);
      toast({ title: "Uploaded!", description: "Image added to library" });
      fetchImageLibrary(selectedFolder, imageSearchQuery);
      return response.data.image;
    } catch (error) {
      console.error('[uploadToLibrary] Upload failed:', error);
      toast({ title: "Error", description: "Failed to upload image", variant: "destructive" });
      return null;
    } finally {
      setUploadingToLibrary(false);
    }
  };

  // Delete image from library
  const deleteImageFromLibrary = async (imageId, force = false) => {
    try {
      const response = await axios.delete(`${API}/images/${imageId}?force=${force}`);
      
      if (response.data.warning && !force) {
        const confirmDelete = window.confirm(
          `This image is used in ${response.data.usage.length} place(s). Delete anyway?`
        );
        if (confirmDelete) {
          return deleteImageFromLibrary(imageId, true);
        }
        return false;
      }
      
      toast({ title: "Deleted", description: "Image removed from library" });
      fetchImageLibrary(selectedFolder, imageSearchQuery);
      setSelectedImageForDetails(null);
      return true;
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete image", variant: "destructive" });
      return false;
    }
  };

  // Replace image in library
  const replaceImageInLibrary = async (imageId, file) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/images/${imageId}/replace`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: "Replaced!", description: "Image updated successfully" });
      fetchImageLibrary(selectedFolder, imageSearchQuery);
      if (selectedImageForDetails?.id === imageId) {
        setSelectedImageForDetails(response.data.image);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to replace image", variant: "destructive" });
    }
  };

  // Update image metadata (folder, tags)
  const updateImageMetadata = async (imageId, updates) => {
    try {
      const params = new URLSearchParams();
      if (updates.folder) params.append('folder', updates.folder);
      if (updates.tags) params.append('tags', JSON.stringify(updates.tags));
      
      await axios.put(`${API}/images/${imageId}?${params.toString()}`);
      toast({ title: "Updated", description: "Image details saved" });
      fetchImageLibrary(selectedFolder, imageSearchQuery);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update image", variant: "destructive" });
    }
  };

  useEffect(() => {
    if (user) {
      fetchAllData();
      fetchImageLibrary();
    }
  }, [user, fetchAllData, fetchImageLibrary]);

  // Handle successful login
  const handleLoginSuccess = (userData, mustChangePassword) => {
    if (mustChangePassword) {
      setShowChangePassword(true);
      toast({ title: "Password Change Required", description: "Please change your password to continue." });
    } else {
      toast({ title: "Welcome!", description: `Signed in as ${userData.name}` });
    }
  };

  // Generic save handler with history and improved error handling
  const handleSave = async (type, item, isNew = false) => {
    try {
      const endpoint = `${API}/${type}${isNew ? '' : `/${item.id}`}`;
      const method = isNew ? 'post' : 'put';
      
      // Clean up list fields - remove empty lines before saving
      const cleanedItem = { ...item };
      Object.keys(cleanedItem).forEach(key => {
        if (Array.isArray(cleanedItem[key])) {
          cleanedItem[key] = cleanedItem[key].filter(line => typeof line === 'string' ? line.trim() : true);
        }
      });
      
      // Log for debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`[CMS] Saving ${type}:`, { 
          endpoint, 
          method, 
          id: item.id, 
          image: item.image, 
          isNew,
          payload: cleanedItem 
        });
      }
      
      const response = await axios[method](endpoint, cleanedItem);
      
      // Log response for debugging
      if (process.env.NODE_ENV === 'development') {
        console.log(`[CMS] Save response:`, response.data);
      }
      
      // Check for actual success from API
      if (response.data.success === false) {
        throw new Error(response.data.message || response.data.detail || 'Save failed');
      }
      
      saveToHistory(`${isNew ? 'create' : 'update'}_${type}`, item);
      
      const displayType = type === 'team' ? 'Team member' : type.slice(0, -1);
      toast({ title: "Saved!", description: `${displayType} ${isNew ? 'created' : 'updated'} successfully` });
      setEditingItem(null);
      setEditingType(null);
      setShowNewForm(false);
      setNewFormType(null);
      fetchAllData();
    } catch (error) {
      console.error(`[CMS] Error saving ${type}:`, error);
      
      // Extract detailed error message
      let errorMessage = `Failed to save ${type === 'team' ? 'team member' : type}`;
      if (error.response) {
        const status = error.response.status;
        let detail = error.response.data?.detail || error.response.data?.message || error.response.statusText;
        
        // Handle Pydantic validation errors (422)
        if (status === 422 && Array.isArray(detail)) {
          const missingFields = detail
            .filter(err => err.type === 'missing' || err.msg?.includes('required'))
            .map(err => {
              const fieldName = err.loc?.[err.loc.length - 1] || 'Unknown field';
              return fieldName.charAt(0).toUpperCase() + fieldName.slice(1).replace(/([A-Z])/g, ' $1');
            });
          
          if (missingFields.length > 0) {
            errorMessage = `Please fill in required fields: ${missingFields.join(', ')}`;
          } else {
            errorMessage = `Validation error: Please check all fields are filled correctly`;
          }
        } else if (typeof detail === 'string') {
          errorMessage = `Error ${status}: ${detail}`;
        } else {
          errorMessage = `Error ${status}: Please check all required fields`;
        }
        
        // Log full error details in development
        if (process.env.NODE_ENV === 'development') {
          console.error('[CMS] Full error response:', error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({ 
        title: "Save Failed", 
        description: errorMessage, 
        variant: "destructive" 
      });
    }
  };

  const handleDelete = async (type, id, name) => {
    if (!window.confirm(`Move "${name || 'this item'}" to trash?`)) return;
    
    try {
      await axios.delete(`${API}/${type}/${id}`);
      saveToHistory(`delete_${type}`, { id, name });
      toast({ title: "Moved to Trash", description: "Item can be restored from the Trash tab" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const handleRestore = async (trashId) => {
    try {
      await axios.post(`${API}/trash/restore/${trashId}`);
      toast({ title: "Restored!", description: "Item has been restored" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to restore", variant: "destructive" });
    }
  };

  const handlePermanentDelete = async (trashId) => {
    if (!window.confirm('Permanently delete this item? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/trash/${trashId}`);
      toast({ title: "Deleted", description: "Item permanently deleted" });
      fetchAllData();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    }
  };

  const handleSettingsSave = async () => {
    try {
      await axios.put(`${API}/settings`, siteSettings);
      saveToHistory('update_settings', siteSettings);
      toast({ title: "Saved!", description: "Site settings updated successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
    }
  };

  // ==================== LOGIN SCREEN ====================
  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Show login form if not authenticated
  if (!user) {
    return <CMSLogin onLoginSuccess={handleLoginSuccess} />;
  }

  // ==================== IMAGE UPLOADER COMPONENT ====================
  const ImageUploader = ({ value, onChange, label, folder = 'Other', acceptVideo = false }) => {
    const fileInputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [showLibrary, setShowLibrary] = useState(false);

    // Handle image/video selection from library or upload
    const handleMediaChange = (newUrl) => {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[ImageUploader] Media changed for ${label}:`, { old: value, new: newUrl });
      }
      onChange(newUrl);
    };

    const handleFileUpload = async (file) => {
      if (!file) return;
      
      // Determine allowed types based on acceptVideo prop
      const imageTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml', 'image/gif'];
      const videoTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
      const allowedTypes = acceptVideo ? [...imageTypes, ...videoTypes] : imageTypes;
      
      if (!allowedTypes.includes(file.type)) {
        const typeDesc = acceptVideo ? 'JPG, PNG, WebP, SVG, GIF, or MP4/WebM video' : 'JPG, PNG, WebP, SVG, or GIF';
        toast({ 
          title: "Invalid File Type", 
          description: `Please upload ${typeDesc}. Received: ${file.type}`, 
          variant: "destructive" 
        });
        return;
      }

      // Increase size limit for videos
      const maxSize = acceptVideo && videoTypes.includes(file.type) ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
      const maxSizeLabel = acceptVideo && videoTypes.includes(file.type) ? '100MB' : '10MB';
      
      if (file.size > maxSize) {
        toast({ 
          title: "File Too Large", 
          description: `File size must be under ${maxSizeLabel}. Current: ${(file.size / 1024 / 1024).toFixed(1)}MB`, 
          variant: "destructive" 
        });
        return;
      }

      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', folder);
      
      // Log upload attempt in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[ImageUploader] Uploading:', { 
          name: file.name, 
          type: file.type, 
          size: file.size, 
          folder 
        });
      }

      try {
        const response = await axios.post(`${API}/images/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000 // 2 minute timeout for large files
        });
        
        if (process.env.NODE_ENV === 'development') {
          console.log('[ImageUploader] Upload response:', response.data);
        }
        
        if (!response.data.image || !response.data.image.url) {
          throw new Error('Upload succeeded but no URL returned');
        }
        
        const mediaUrl = `${BACKEND_URL}${response.data.image.url}`;
        handleMediaChange(mediaUrl);
        toast({ title: "Uploaded!", description: `${file.type.startsWith('video/') ? 'Video' : 'Image'} added successfully` });
        
        // Refresh image library
        fetchImageLibrary(selectedFolder, imageSearchQuery);
      } catch (error) {
        console.error('[ImageUploader] Upload error:', error);
        
        let errorMessage = 'Failed to upload file';
        if (error.response) {
          errorMessage = `Error ${error.response.status}: ${error.response.data?.detail || error.response.statusText}`;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        toast({ 
          title: "Upload Failed", 
          description: errorMessage, 
          variant: "destructive" 
        });
      } finally {
        setUploading(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      handleFileUpload(file);
    };

    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      for (let item of items) {
        if (item.type.startsWith('image/') || (acceptVideo && item.type.startsWith('video/'))) {
          const file = item.getAsFile();
          handleFileUpload(file);
          break;
        }
      }
    };
    
    const isVideo = value && (value.endsWith('.mp4') || value.endsWith('.webm') || value.includes('video'));
    const acceptString = acceptVideo 
      ? 'image/jpeg,image/png,image/webp,image/svg+xml,image/gif,video/mp4,video/webm' 
      : 'image/jpeg,image/png,image/webp,image/svg+xml,image/gif';

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        
        {/* Choose from Library Button */}
        <Button 
          type="button"
          variant="outline" 
          className="w-full border-blue-500 text-blue-600 hover:bg-blue-50"
          onClick={() => setShowLibrary(true)}
        >
          <ImageIcon className="w-4 h-4 mr-2" /> Choose from Library
        </Button>

        {/* Image Library Modal */}
        <ImageLibraryModal
          isOpen={showLibrary}
          onClose={() => setShowLibrary(false)}
          onSelect={(url) => handleMediaChange(url)}
          initialFolder={folder}
        />
        
        {/* Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer
            ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
            ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onPaste={handlePaste}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptString}
            className="hidden"
            onChange={(e) => handleFileUpload(e.target.files[0])}
          />
          
          {uploading ? (
            <div className="flex items-center justify-center gap-2 text-blue-600">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span>Uploading...</span>
            </div>
          ) : (
            <div className="space-y-2">
              <Upload className="w-8 h-8 mx-auto text-gray-400" />
              <p className="text-sm text-gray-600">
                <span className="text-blue-600 font-medium">Click to upload new</span>, drag & drop, or paste
              </p>
              <p className="text-xs text-gray-400">
                {acceptVideo ? 'Images (JPG, PNG, WebP) up to 10MB, Videos (MP4) up to 100MB' : 'JPG, PNG, WebP, SVG, GIF up to 10MB'}
              </p>
            </div>
          )}
        </div>

        {/* URL Input */}
        <div className="flex gap-2">
          <Input 
            value={value || ''} 
            onChange={(e) => handleMediaChange(e.target.value)} 
            placeholder="Or paste media URL"
            className="flex-1"
          />
        </div>

        {/* Preview */}
        {value && (
          <div className="mt-2 relative w-full h-40 bg-gray-100 rounded-lg overflow-hidden group">
            {isVideo ? (
              <video 
                src={value} 
                className="w-full h-full object-contain"
                muted
                loop
                playsInline
                onMouseEnter={(e) => e.target.play()}
                onMouseLeave={(e) => e.target.pause()}
              />
            ) : (
              <img src={value} alt="Preview" className="w-full h-full object-contain" />
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button 
                onClick={(e) => { e.stopPropagation(); setShowLibrary(true); }}
                className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600"
                title="Replace from library"
              >
                <Replace className="w-5 h-5" />
              </button>
              <button 
                onClick={(e) => { e.stopPropagation(); handleMediaChange(''); }}
                className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600"
                title="Remove"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
            <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
              {isVideo ? 'Video Preview' : 'Preview'}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==================== LOGO EDITOR COMPONENT ====================
  const LogoEditor = () => {
    const [previewDesktop, setPreviewDesktop] = useState(siteSettings.logoSizeDesktop || 120);
    const [previewMobile, setPreviewMobile] = useState(siteSettings.logoSizeMobile || 80);

    return (
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gray-50">
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5" /> Logo Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Logo Upload */}
          <ImageUploader 
            label="Company Logo"
            value={siteSettings.logoUrl}
            onChange={(val) => setSiteSettings({...siteSettings, logoUrl: val})}
            folder="Logos"
          />

          {/* Size Controls */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Desktop Size */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Desktop Size: {previewDesktop}px
              </label>
              <input
                type="range"
                min="60"
                max="200"
                value={previewDesktop}
                onChange={(e) => setPreviewDesktop(parseInt(e.target.value))}
                onMouseUp={() => setSiteSettings({...siteSettings, logoSizeDesktop: previewDesktop})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>60px</span>
                <span>200px</span>
              </div>
              {/* Desktop Preview */}
              <div className="bg-gray-900 p-4 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">Desktop Preview</p>
                {siteSettings.logoUrl ? (
                  <img 
                    src={siteSettings.logoUrl} 
                    alt="Logo" 
                    style={{ height: `${previewDesktop}px`, width: 'auto' }}
                    className="object-contain"
                  />
                ) : (
                  <div className="text-gray-500 text-sm">No logo uploaded</div>
                )}
              </div>
            </div>

            {/* Mobile Size */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Mobile Size: {previewMobile}px
              </label>
              <input
                type="range"
                min="40"
                max="120"
                value={previewMobile}
                onChange={(e) => setPreviewMobile(parseInt(e.target.value))}
                onMouseUp={() => setSiteSettings({...siteSettings, logoSizeMobile: previewMobile})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500">
                <span>40px</span>
                <span>120px</span>
              </div>
              {/* Mobile Preview */}
              <div className="bg-gray-900 p-4 rounded-lg max-w-[320px]">
                <p className="text-xs text-gray-400 mb-2">Mobile Preview</p>
                {siteSettings.logoUrl ? (
                  <img 
                    src={siteSettings.logoUrl} 
                    alt="Logo" 
                    style={{ height: `${previewMobile}px`, width: 'auto' }}
                    className="object-contain"
                  />
                ) : (
                  <div className="text-gray-500 text-sm">No logo uploaded</div>
                )}
              </div>
            </div>
          </div>

          <p className="text-sm text-gray-500">
            Logo will automatically maintain aspect ratio. Changes preview instantly.
          </p>
        </CardContent>
      </Card>
    );
  };

  // ==================== IMAGE LIBRARY MODAL ====================
  const ImageLibraryModal = ({ isOpen, onClose, onSelect, initialFolder = 'All' }) => {
    const [localFolder, setLocalFolder] = useState(initialFolder);
    const [localSearch, setLocalSearch] = useState('');
    const [localImages, setLocalImages] = useState([]);
    const [localFolders, setLocalFolders] = useState([]);
    const [localFolderCounts, setLocalFolderCounts] = useState({});
    const [loadingImages, setLoadingImages] = useState(false);
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const dropZoneRef = useRef(null);

    // Fetch images for modal
    useEffect(() => {
      if (!isOpen) return;
      
      const fetchImages = async () => {
        setLoadingImages(true);
        try {
          const params = new URLSearchParams();
          if (localFolder && localFolder !== 'All') params.append('folder', localFolder);
          if (localSearch) params.append('search', localSearch);
          
          const response = await axios.get(`${API}/images?${params.toString()}`);
          setLocalImages(response.data.images || []);
          setLocalFolders(response.data.folders || ['All', 'Homepage', 'Services', 'Projects', 'Sectors', 'Team', 'Logos', 'Other']);
          setLocalFolderCounts(response.data.folderCounts || {});
        } catch (error) {
          console.error('Error fetching images:', error);
        } finally {
          setLoadingImages(false);
        }
      };
      
      fetchImages();
    }, [isOpen, localFolder, localSearch]);

    const handleUploadInModal = async (file) => {
      console.log('[ImageLibrary Modal] handleUploadInModal called with file:', file?.name, file?.type, file?.size);
      if (!file) {
        console.log('[ImageLibrary Modal] No file provided');
        return;
      }
      const image = await uploadToLibrary(file, localFolder !== 'All' ? localFolder : 'Other');
      console.log('[ImageLibrary Modal] Upload result:', image);
      if (image) {
        // Refresh the modal's image list
        const params = new URLSearchParams();
        if (localFolder && localFolder !== 'All') params.append('folder', localFolder);
        const response = await axios.get(`${API}/images?${params.toString()}`);
        setLocalImages(response.data.images || []);
        setLocalFolderCounts(response.data.folderCounts || {});
        console.log('[ImageLibrary Modal] Library refreshed with', response.data.images?.length, 'images');
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      setIsDraggingOver(false);
      const file = e.dataTransfer.files[0];
      if (file && file.type.startsWith('image/')) {
        handleUploadInModal(file);
      }
    };

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">Image Library</h2>
                <p className="text-sm text-gray-500">Select an image or upload a new one</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar - Folders */}
            <div className="w-48 border-r bg-gray-50 p-3 space-y-1 overflow-y-auto">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Folders</p>
              {localFolders.map((folder) => (
                <button
                  key={folder}
                  onClick={() => setLocalFolder(folder)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-colors
                    ${localFolder === folder ? 'bg-blue-600 text-white' : 'hover:bg-gray-200 text-gray-700'}`}
                >
                  <span className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4" />
                    {folder}
                  </span>
                  <span className={`text-xs ${localFolder === folder ? 'text-blue-100' : 'text-gray-400'}`}>
                    {localFolderCounts[folder] || 0}
                  </span>
                </button>
              ))}
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Search & Upload */}
              <div className="p-4 border-b flex gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={localSearch}
                    onChange={(e) => setLocalSearch(e.target.value)}
                    placeholder="Search images..."
                    className="pl-9"
                  />
                </div>
                <input
                  type="file"
                  id="modal-upload-input"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    console.log('[ImageLibrary Modal] File selected:', e.target.files?.[0]?.name);
                    handleUploadInModal(e.target.files[0]);
                    e.target.value = ''; // Reset to allow re-selecting same file
                  }}
                />
                <Button 
                  type="button"
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    console.log('[ImageLibrary Modal] Upload button clicked');
                    document.getElementById('modal-upload-input')?.click();
                  }}
                >
                  <Upload className="w-4 h-4 mr-2" /> Upload
                </Button>
              </div>

              {/* Drop Zone & Image Grid */}
              <div 
                ref={dropZoneRef}
                className={`flex-1 overflow-y-auto p-4 transition-colors ${isDraggingOver ? 'bg-blue-50' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setIsDraggingOver(true); }}
                onDragLeave={() => setIsDraggingOver(false)}
                onDrop={handleDrop}
              >
                {isDraggingOver && (
                  <div className="absolute inset-4 border-2 border-dashed border-blue-500 rounded-xl bg-blue-50/80 flex items-center justify-center z-10">
                    <div className="text-center">
                      <Upload className="w-12 h-12 mx-auto text-blue-500 mb-2" />
                      <p className="text-blue-600 font-medium">Drop image here to upload</p>
                    </div>
                  </div>
                )}

                {loadingImages ? (
                  <div className="flex items-center justify-center py-20">
                    <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : localImages.length === 0 ? (
                  <div className="text-center py-20">
                    <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">No images in this folder</p>
                    <p className="text-sm text-gray-400 mt-1">Upload or drag & drop images here</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                    {localImages.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => {
                          const imageUrl = img.url.startsWith('http') ? img.url : `${BACKEND_URL}${img.url}`;
                          onSelect(imageUrl, img);
                          onClose();
                        }}
                        className="group relative aspect-square bg-gray-100 rounded-lg overflow-hidden hover:ring-2 hover:ring-blue-500 focus:ring-2 focus:ring-blue-500 transition-all"
                      >
                        <img 
                          src={img.url.startsWith('http') ? img.url : `${BACKEND_URL}${img.url}`} 
                          alt={img.originalName} 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <Check className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <p className="text-white text-xs truncate">{img.originalName}</p>
                        </div>
                        {img.usedIn && img.usedIn.length > 0 && (
                          <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded">
                            In use
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ==================== IMAGE LIBRARY TAB ====================
  const ImageLibraryTab = () => {
    const [isDragging, setIsDragging] = useState(false);
    const [bulkUploading, setBulkUploading] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
      fetchImageLibrary(selectedFolder, imageSearchQuery);
    }, [selectedFolder, imageSearchQuery, fetchImageLibrary]);

    const handleBulkUpload = async (files) => {
      if (!files || files.length === 0) return;
      
      setBulkUploading(true);
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('files', file));
      formData.append('folder', selectedFolder !== 'All' ? selectedFolder : 'Other');

      try {
        const response = await axios.post(`${API}/images/bulk-upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast({ 
          title: "Upload Complete", 
          description: `${response.data.count} images uploaded${response.data.errors.length > 0 ? `, ${response.data.errors.length} failed` : ''}` 
        });
        fetchImageLibrary(selectedFolder, imageSearchQuery);
      } catch (error) {
        toast({ title: "Error", description: "Failed to upload images", variant: "destructive" });
      } finally {
        setBulkUploading(false);
      }
    };

    const handleDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      handleBulkUpload(e.dataTransfer.files);
    };

    const formatFileSize = (bytes) => {
      if (bytes < 1024) return bytes + ' B';
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Image Library</h2>
            <p className="text-gray-500">Manage all images used across your website</p>
          </div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                console.log('[ImageLibrary Tab] Files selected:', e.target.files?.length);
                handleBulkUpload(e.target.files);
                e.target.value = ''; // Reset to allow re-selecting same file
              }}
            />
            <Button 
              type="button"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                console.log('[ImageLibrary Tab] Upload button clicked');
                fileInputRef.current?.click();
              }}
            >
              <Upload className="w-4 h-4 mr-2" /> Upload Images
            </Button>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar - Folders */}
          <div className="w-56 shrink-0">
            <Card className="border-0 shadow-lg sticky top-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-gray-500 uppercase flex items-center justify-between">
                  Folders
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => {
                      const folderName = prompt('Enter new folder name:');
                      if (folderName && folderName.trim()) {
                        // Add folder via API
                        axios.post(`${API}/images/folders`, { name: folderName.trim() })
                          .then(() => {
                            toast({ title: "Created!", description: `Folder "${folderName}" created` });
                            fetchImageLibrary(selectedFolder, imageSearchQuery);
                          })
                          .catch(err => {
                            toast({ 
                              title: "Failed", 
                              description: err.response?.data?.detail || "Could not create folder", 
                              variant: "destructive" 
                            });
                          });
                      }
                    }}
                    title="Create new folder"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-2 space-y-1">
                {imageFolders.map((folder) => {
                  const isSystem = ['All', 'Homepage', 'Services', 'Projects', 'Sectors', 'Team', 'Logos'].includes(folder);
                  return (
                    <div key={folder} className="group relative">
                      <button
                        onClick={() => {
                          setSelectedFolder(folder);
                          fetchImageLibrary(folder, imageSearchQuery);
                        }}
                        className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center justify-between transition-colors
                          ${selectedFolder === folder ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'}`}
                      >
                        <span className="flex items-center gap-2">
                          <FolderOpen className="w-4 h-4" />
                          {folder}
                        </span>
                        <span className={`text-xs font-medium ${selectedFolder === folder ? 'text-blue-100' : 'text-gray-400'}`}>
                          {folderCounts[folder] || 0}
                        </span>
                      </button>
                      {/* Folder actions (only for non-system folders) */}
                      {!isSystem && selectedFolder === folder && (
                        <div className="flex items-center gap-1 mt-1 px-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-gray-500 hover:text-blue-600"
                            onClick={() => {
                              const newName = prompt('Rename folder:', folder);
                              if (newName && newName.trim() && newName !== folder) {
                                axios.put(`${API}/images/folders/${encodeURIComponent(folder)}`, { name: newName.trim() })
                                  .then(() => {
                                    toast({ title: "Renamed!", description: `Folder renamed to "${newName}"` });
                                    setSelectedFolder(newName.trim());
                                    fetchImageLibrary(newName.trim(), imageSearchQuery);
                                  })
                                  .catch(err => {
                                    toast({ 
                                      title: "Rename Failed", 
                                      description: err.response?.data?.detail || "Could not rename folder", 
                                      variant: "destructive" 
                                    });
                                  });
                              }
                            }}
                          >
                            <Edit3 className="w-3 h-3 mr-1" /> Rename
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => {
                              const count = folderCounts[folder] || 0;
                              const confirmMsg = count > 0 
                                ? `Delete folder "${folder}" and move ${count} images to "Other"?`
                                : `Delete empty folder "${folder}"?`;
                              if (confirm(confirmMsg)) {
                                axios.delete(`${API}/images/folders/${encodeURIComponent(folder)}`)
                                  .then(() => {
                                    toast({ title: "Deleted!", description: `Folder "${folder}" deleted` });
                                    setSelectedFolder('All');
                                    fetchImageLibrary('All', imageSearchQuery);
                                  })
                                  .catch(err => {
                                    toast({ 
                                      title: "Delete Failed", 
                                      description: err.response?.data?.detail || "Could not delete folder", 
                                      variant: "destructive" 
                                    });
                                  });
                              }
                            }}
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Delete
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="flex-1 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={imageSearchQuery}
                onChange={(e) => setImageSearchQuery(e.target.value)}
                placeholder="Search by filename or tag..."
                className="pl-9"
              />
            </div>

            {/* Drop Zone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer
                ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 bg-gray-50'}
                ${bulkUploading ? 'opacity-50 pointer-events-none' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              {bulkUploading ? (
                <div className="flex items-center justify-center gap-2 text-blue-600">
                  <RefreshCw className="w-6 h-6 animate-spin" />
                  <span className="font-medium">Uploading...</span>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                  <p className="text-gray-600">
                    <span className="text-blue-600 font-medium">Click to upload</span> or drag & drop
                  </p>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG, WebP, SVG, GIF up to 10MB each</p>
                </>
              )}
            </div>

            {/* Image Grid */}
            {imageLibrary.length === 0 ? (
              <Card className="border-0 shadow-lg">
                <CardContent className="p-12 text-center">
                  <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500 text-lg">No images in this folder</p>
                  <p className="text-gray-400 text-sm mt-2">Upload some images to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {imageLibrary.map((img) => (
                  <Card 
                    key={img.id} 
                    className={`border-0 shadow-lg overflow-hidden cursor-pointer group
                      ${selectedImageForDetails?.id === img.id ? 'ring-2 ring-blue-500 shadow-xl' : 'hover:shadow-xl'}`}
                    onClick={() => setSelectedImageForDetails(img)}
                    style={{ transition: 'box-shadow 0.2s ease, transform 0.15s ease' }}
                  >
                    <div className="aspect-square bg-gray-100 relative overflow-hidden">
                      <img 
                        src={img.url.startsWith('http') ? img.url : `${BACKEND_URL}${img.url}`} 
                        alt={img.originalName}
                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                        loading="lazy"
                      />
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-200 pointer-events-none" />
                      {img.usedIn && img.usedIn.length > 0 && (
                        <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow-md">
                          <Check className="w-3 h-3" />
                          {img.usedIn.length}
                        </div>
                      )}
                      {selectedImageForDetails?.id === img.id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-blue-500/20 pointer-events-none">
                          <Check className="w-8 h-8 text-white drop-shadow-lg" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-3">
                      <p className="text-sm font-medium text-gray-900 truncate" title={img.originalName}>{img.originalName}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-gray-400">{formatFileSize(img.size)}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{img.folder}</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            
            {/* Pagination - Load More */}
            {imagePagination.hasMore && (
              <div className="text-center py-6">
                <Button 
                  variant="outline"
                  size="lg"
                  onClick={loadMoreImages}
                  disabled={loadingMoreImages}
                  className="min-w-[200px]"
                >
                  {loadingMoreImages ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Load More ({imagePagination.total - imageLibrary.length} remaining)
                    </>
                  )}
                </Button>
              </div>
            )}
            
            {/* Image count summary */}
            {imageLibrary.length > 0 && (
              <p className="text-sm text-gray-500 text-center">
                Showing {imageLibrary.length} of {imagePagination.total} images
              </p>
            )}
          </div>

          {/* Image Details Panel */}
          {selectedImageForDetails && (
            <div className="w-72 shrink-0">
              <Card className="border-0 shadow-lg sticky top-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span>Image Details</span>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedImageForDetails(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Preview */}
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                    <img 
                      src={selectedImageForDetails.url.startsWith('http') ? selectedImageForDetails.url : `${BACKEND_URL}${selectedImageForDetails.url}`}
                      alt={selectedImageForDetails.originalName}
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Info */}
                  <div className="space-y-2 text-sm">
                    <div>
                      <p className="text-gray-500">Filename</p>
                      <p className="font-medium truncate">{selectedImageForDetails.originalName}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Size</p>
                      <p className="font-medium">{formatFileSize(selectedImageForDetails.size)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Folder</p>
                      <select
                        value={selectedImageForDetails.folder}
                        onChange={(e) => {
                          updateImageMetadata(selectedImageForDetails.id, { folder: e.target.value });
                          setSelectedImageForDetails({...selectedImageForDetails, folder: e.target.value});
                        }}
                        className="w-full border rounded p-1.5 text-sm mt-1"
                      >
                        {imageFolders.filter(f => f !== 'All').map(f => (
                          <option key={f} value={f}>{f}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <p className="text-gray-500">Usage</p>
                      <p className="font-medium">
                        {selectedImageForDetails.usedIn?.length || 0} place(s)
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2 pt-2">
                    {/* Copy URL */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full"
                      onClick={() => {
                        const url = selectedImageForDetails.url.startsWith('http') 
                          ? selectedImageForDetails.url 
                          : `${BACKEND_URL}${selectedImageForDetails.url}`;
                        navigator.clipboard.writeText(url);
                        toast({ title: "Copied!", description: "Image URL copied to clipboard" });
                      }}
                    >
                      Copy URL
                    </Button>

                    {/* Replace */}
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => replaceImageInLibrary(selectedImageForDetails.id, e.target.files[0])}
                      />
                      <Button as="span" variant="outline" size="sm" className="w-full">
                        <Replace className="w-4 h-4 mr-2" /> Replace
                      </Button>
                    </label>

                    {/* Delete */}
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full border-red-500 text-red-500 hover:bg-red-50"
                      onClick={() => {
                        if (window.confirm('Delete this image?')) {
                          deleteImageFromLibrary(selectedImageForDetails.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ==================== TRASH TAB ====================
  const TrashTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Recycle Bin</h2>
          <p className="text-gray-500">Restore or permanently delete items</p>
        </div>
        {trashItems.length > 0 && (
          <Button 
            variant="outline" 
            className="border-red-500 text-red-500 hover:bg-red-50"
            onClick={async () => {
              if (!window.confirm('Empty trash? This cannot be undone.')) return;
              try {
                await axios.delete(`${API}/trash`);
                toast({ title: "Trash Emptied", description: "All items permanently deleted" });
                fetchAllData();
              } catch (e) {
                toast({ title: "Error", description: "Failed to empty trash", variant: "destructive" });
              }
            }}
          >
            <Trash2 className="w-4 h-4 mr-2" /> Empty Trash
          </Button>
        )}
      </div>

      {trashItems.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-12 text-center">
            <Trash2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg">Trash is empty</p>
            <p className="text-gray-400 text-sm mt-2">Deleted items will appear here for recovery</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {trashItems.map((item) => (
            <Card key={item.trashId} className="border-0 shadow-lg">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded font-medium uppercase">
                      {item.itemType}
                    </span>
                    <span className="font-semibold text-gray-900">{item.itemName}</span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Deleted {new Date(item.deletedAt).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    onClick={() => handleRestore(item.trashId)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Restore
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => handlePermanentDelete(item.trashId)}
                    className="border-red-500 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // ==================== MEDIA HEALTH TAB ====================
  const MediaHealthTab = () => {
    const [cleaningUp, setCleaningUp] = useState(false);
    
    const checkMediaHealth = async () => {
      setMediaHealthLoading(true);
      try {
        const response = await axios.get(`${API}/media/health`);
        setMediaHealth(response.data);
      } catch (error) {
        toast({ title: "Error", description: "Failed to check media health", variant: "destructive" });
      } finally {
        setMediaHealthLoading(false);
      }
    };
    
    const cleanupBrokenMedia = async () => {
      if (!window.confirm('This will permanently remove database records for all broken media files. The actual files are already missing. Continue?')) {
        return;
      }
      
      setCleaningUp(true);
      try {
        const response = await axios.delete(`${API}/media/cleanup-broken`);
        toast({ 
          title: "Cleanup Complete", 
          description: `Removed ${response.data.deletedCount} broken media records` 
        });
        // Refresh the health check
        await checkMediaHealth();
      } catch (error) {
        toast({ title: "Error", description: "Failed to clean up broken media", variant: "destructive" });
      } finally {
        setCleaningUp(false);
      }
    };

    useEffect(() => {
      checkMediaHealth();
    }, []);

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Media Health Check</h2>
            <p className="text-gray-500">Monitor and fix broken media assets</p>
          </div>
          <Button onClick={checkMediaHealth} disabled={mediaHealthLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${mediaHealthLoading ? 'animate-spin' : ''}`} />
            {mediaHealthLoading ? 'Checking...' : 'Run Check'}
          </Button>
        </div>

        {mediaHealth && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Media</p>
                      <p className="text-3xl font-bold text-gray-900">{mediaHealth.summary?.total || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <ImageIcon className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Healthy</p>
                      <p className="text-3xl font-bold text-green-600">{mediaHealth.summary?.healthy || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Check className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Cloudinary</p>
                      <p className="text-3xl font-bold text-purple-600">{mediaHealth.summary?.cloudinary || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                      <Globe className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Broken</p>
                      <p className="text-3xl font-bold text-red-600">{mediaHealth.summary?.broken || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Status Banner */}
            {mediaHealth.summary?.broken === 0 ? (
              <Card className="border-0 shadow-lg bg-green-50">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-800">All Media Healthy</h3>
                    <p className="text-sm text-green-600">No broken or missing files detected.</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Recommendations */}
                {mediaHealth.recommendations?.length > 0 && (
                  <Card className="border-0 shadow-lg bg-amber-50">
                    <CardHeader>
                      <CardTitle className="text-amber-800 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" /> Recommendations
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="list-disc list-inside space-y-1 text-amber-700">
                        {mediaHealth.recommendations.map((rec, i) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Broken Media List */}
                <Card className="border-0 shadow-lg">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-red-800 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" /> Broken Media ({mediaHealth.brokenMedia?.length || 0})
                    </CardTitle>
                    {mediaHealth.brokenMedia?.length > 0 && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={cleanupBrokenMedia}
                        disabled={cleaningUp}
                      >
                        <Trash2 className={`w-4 h-4 mr-2 ${cleaningUp ? 'animate-spin' : ''}`} />
                        {cleaningUp ? 'Cleaning...' : 'Clean Up All Broken'}
                      </Button>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {mediaHealth.brokenMedia?.map((item, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-red-100 rounded flex items-center justify-center">
                              <ImageIcon className="w-5 h-5 text-red-400" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{item.originalName || item.filename}</p>
                              <p className="text-xs text-gray-500">
                                Folder: {item.folder || 'Uncategorized'} | Issue: {item.issue}
                              </p>
                              {item.usedIn?.length > 0 && (
                                <p className="text-xs text-red-600">Used in: {item.usedIn.join(', ')}</p>
                              )}
                            </div>
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setActiveTab('images');
                              toast({ title: "Navigate to Image Library", description: "Re-upload the missing file" });
                            }}
                          >
                            <Upload className="w-4 h-4 mr-1" /> Re-upload
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
        )}

        {!mediaHealth && !mediaHealthLoading && (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">Click "Run Check" to scan media assets</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // ==================== DASHBOARD TAB ====================
  const DashboardTab = () => (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {[
          { label: 'Images', count: folderCounts['All'] || imageLibrary.length, icon: ImageIcon, color: 'blue' },
          { label: 'Services', count: services.length, icon: Wrench, color: 'green' },
          { label: 'Projects', count: projects.length, icon: FolderOpen, color: 'purple' },
          { label: 'Sectors', count: sectors.length, icon: Layers, color: 'orange' },
          { label: 'Team', count: teamMembers.length, icon: Users, color: 'cyan' },
          { label: 'In Trash', count: trashItems.length, icon: Trash2, color: 'gray' },
        ].map((stat, idx) => (
          <Card key={idx} className="border-0 shadow-lg">
            <CardContent className="p-4 text-center">
              <stat.icon className={`w-6 h-6 mx-auto mb-2 text-${stat.color}-600`} />
              <div className="text-2xl font-bold text-gray-900">{stat.count}</div>
              <div className="text-xs text-gray-500">{stat.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {/* Quick Actions */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" /> Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-5 gap-4">
          <Button onClick={() => setActiveTab('images')} variant="outline" className="h-16 flex-col gap-1">
            <ImageIcon className="w-5 h-5" />
            <span className="text-xs">Image Library</span>
          </Button>
          <Button onClick={() => setActiveTab('homepage')} variant="outline" className="h-16 flex-col gap-1">
            <Home className="w-5 h-5" />
            <span className="text-xs">Edit Homepage</span>
          </Button>
          <Button onClick={() => setActiveTab('services')} variant="outline" className="h-16 flex-col gap-1">
            <Wrench className="w-5 h-5" />
            <span className="text-xs">Manage Services</span>
          </Button>
          <Button onClick={() => setActiveTab('projects')} variant="outline" className="h-16 flex-col gap-1">
            <FolderOpen className="w-5 h-5" />
            <span className="text-xs">Manage Projects</span>
          </Button>
          <Button onClick={() => setActiveTab('trash')} variant="outline" className="h-16 flex-col gap-1">
            <Trash2 className="w-5 h-5" />
            <span className="text-xs">View Trash ({trashItems.length})</span>
          </Button>
        </CardContent>
      </Card>

      {/* How to Use */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle>How to Use This CMS</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-sm max-w-none">
          <ul className="space-y-2 text-gray-600 text-sm">
            <li><strong>Image Library:</strong> Central hub for all images. Upload, organise by folder, search, and manage</li>
            <li><strong>Choose from Library:</strong> When editing content, click &quot;Choose from Library&quot; to select existing images</li>
            <li><strong>Upload Images:</strong> Drag & drop, paste, or click to upload. Supported: JPG, PNG, WebP, SVG, GIF</li>
            <li><strong>Logo Settings:</strong> Use sliders to adjust logo size for desktop and mobile with instant preview</li>
            <li><strong>Media Health:</strong> Check for broken or missing images under System → Media Health</li>
            <li><strong>Recycle Bin:</strong> Deleted items go to trash and can be restored</li>
            <li><strong>All changes save immediately</strong> when you click Save buttons</li>
          </ul>
        </CardContent>
      </Card>

      {/* Recent Activity / Version History */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5" /> Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RecentActivityPanel />
        </CardContent>
      </Card>
    </div>
  );

  // Recent Activity Panel Component
  const RecentActivityPanel = () => {
    const [recentHistory, setRecentHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);

    useEffect(() => {
      const fetchHistory = async () => {
        try {
          // Fetch history from multiple collections
          const collections = ['site_settings', 'services', 'projects', 'team', 'sectors'];
          const allHistory = [];
          
          for (const collection of collections) {
            try {
              const response = await axios.get(`${API}/history/${collection}`);
              if (response.data.history) {
                allHistory.push(...response.data.history.map(h => ({ ...h, collection })));
              }
            } catch (e) {
              // Collection might not have history
            }
          }
          
          // Sort by timestamp and take recent 10
          allHistory.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
          setRecentHistory(allHistory.slice(0, 10));
        } catch (error) {
          console.error('Error fetching history:', error);
        } finally {
          setLoadingHistory(false);
        }
      };
      
      fetchHistory();
    }, []);

    const handleRestore = async (versionId) => {
      if (!window.confirm('Restore this version? This will overwrite current content.')) return;
      try {
        await axios.post(`${API}/history/restore/${versionId}`);
        toast({ title: "Restored!", description: "Version restored successfully. Refreshing data..." });
        fetchAllData();
      } catch (error) {
        toast({ title: "Error", description: "Failed to restore version", variant: "destructive" });
      }
    };

    if (loadingHistory) {
      return (
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      );
    }

    if (recentHistory.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <History className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>No recent changes recorded</p>
          <p className="text-sm text-gray-400">Changes will appear here as you edit content</p>
        </div>
      );
    }

    return (
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {recentHistory.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div className="flex items-center gap-3">
              <div className={`w-2 h-2 rounded-full ${
                item.action === 'create' ? 'bg-green-500' : 
                item.action === 'delete' ? 'bg-red-500' : 'bg-blue-500'
              }`} />
              <div>
                <p className="text-sm font-medium text-gray-700">
                  {item.action === 'create' ? 'Created' : item.action === 'delete' ? 'Deleted' : 'Updated'}{' '}
                  <span className="text-blue-600">{item.collection?.replace('_', ' ')}</span>
                  {item.itemId && item.itemId !== 'site_settings' && (
                    <span className="text-gray-500"> - {item.itemId}</span>
                  )}
                </p>
                <p className="text-xs text-gray-400">
                  {new Date(item.timestamp).toLocaleString()}
                </p>
              </div>
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => handleRestore(item.versionId)}
              title="Restore this version"
            >
              <Undo2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    );
  };

  // ==================== HOMEPAGE TAB ====================
  const HomepageTab = () => {
    // Use local state for form fields to prevent scroll jumps during typing
    const [localHeroTitle, setLocalHeroTitle] = useState(siteSettings.heroTitle || '');
    const [localHeroSubtitle, setLocalHeroSubtitle] = useState(siteSettings.heroSubtitle || '');
    const [localAboutDescription, setLocalAboutDescription] = useState(siteSettings.aboutDescription || '');
    
    // State for per-character color editing
    const [heroColorSelection, setHeroColorSelection] = useState([]);
    const [heroApplyColor, setHeroApplyColor] = useState('#FF0000');
    
    // Sync local state when siteSettings changes from external source
    useEffect(() => {
      setLocalHeroTitle(siteSettings.heroTitle || '');
      setLocalHeroSubtitle(siteSettings.heroSubtitle || '');
      setLocalAboutDescription(siteSettings.aboutDescription || '');
    }, [siteSettings.heroTitle, siteSettings.heroSubtitle, siteSettings.aboutDescription]);

    // Update main state on blur (when user finishes typing)
    const handleBlur = (field, value) => {
      setSiteSettings(prev => ({...prev, [field]: value}));
    };

    return (
      <div className="space-y-6">
        {/* Logo Settings */}
        <LogoEditor />

        {/* Hero Section */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Home className="w-5 h-5" /> Hero Section
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {/* Hero Logo Settings */}
            <div className="border rounded-lg p-4 bg-blue-50/50">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" /> Hero Logo
                </h4>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={siteSettings.heroLogoEnabled || false}
                    onChange={(e) => setSiteSettings({...siteSettings, heroLogoEnabled: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Show Logo Above Title</span>
                </label>
              </div>
              
              {siteSettings.heroLogoEnabled && (
                <div className="space-y-4">
                  <ImageUploader 
                    label="Hero Logo Image"
                    value={siteSettings.heroLogoUrl}
                    onChange={(val) => setSiteSettings({
                      ...siteSettings, 
                      heroLogoUrl: val,
                      heroLogoVersion: (siteSettings.heroLogoVersion || 1) + 1
                    })}
                    folder="Logos"
                  />
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Desktop Size: {siteSettings.heroLogoSizeDesktop || 120}px
                      </label>
                      <input 
                        type="range" 
                        min="40" 
                        max="200" 
                        value={siteSettings.heroLogoSizeDesktop || 120}
                        onChange={(e) => setSiteSettings({...siteSettings, heroLogoSizeDesktop: parseInt(e.target.value)})}
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Mobile Size: {siteSettings.heroLogoSizeMobile || 80}px
                      </label>
                      <input 
                        type="range" 
                        min="30" 
                        max="150" 
                        value={siteSettings.heroLogoSizeMobile || 80}
                        onChange={(e) => setSiteSettings({...siteSettings, heroLogoSizeMobile: parseInt(e.target.value)})}
                        className="w-full"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Max Width (px)</label>
                      <Input 
                        type="number"
                        value={siteSettings.heroLogoMaxWidth || 300}
                        onChange={(e) => setSiteSettings({...siteSettings, heroLogoMaxWidth: parseInt(e.target.value) || 300})}
                        min="100"
                        max="600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Alignment</label>
                      <select 
                        value={siteSettings.heroLogoAlignment || 'center'}
                        onChange={(e) => setSiteSettings({...siteSettings, heroLogoAlignment: e.target.value})}
                        className="w-full border rounded-md p-2"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Spacing Below: {siteSettings.heroLogoSpacing || 24}px
                      </label>
                      <input 
                        type="range" 
                        min="0" 
                        max="60" 
                        value={siteSettings.heroLogoSpacing || 24}
                        onChange={(e) => setSiteSettings({...siteSettings, heroLogoSpacing: parseInt(e.target.value)})}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Hero Title */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-4">Hero Title</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title Text *</label>
                  <Input 
                    value={localHeroTitle} 
                    onChange={(e) => {
                      setLocalHeroTitle(e.target.value);
                      // Adjust color spans when text changes
                      const newSpans = (siteSettings.heroTitleColorSpans || []).filter(
                        span => span.startIndex < e.target.value.length
                      ).map(span => ({
                        ...span,
                        endIndex: Math.min(span.endIndex, e.target.value.length)
                      }));
                      setSiteSettings({...siteSettings, heroTitleColorSpans: newSpans});
                    }}
                    onBlur={(e) => handleBlur('heroTitle', e.target.value)}
                    placeholder="Main headline on homepage"
                    className="text-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Use \n for line breaks (e.g., &quot;Engineering\nExcellence&quot;)</p>
                </div>
                
                {/* Per-Character Color Editor */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Per-Character Color Styling
                    </label>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSiteSettings({...siteSettings, heroTitleColorSpans: []})}
                      className="text-xs"
                    >
                      Clear All Colors
                    </Button>
                  </div>
                  
                  {/* Character Selection Preview */}
                  <div className="bg-gray-900 rounded-lg p-4 mb-3 overflow-x-auto">
                    <div className="flex flex-wrap gap-0.5 font-mono text-lg">
                      {(localHeroTitle || '').split('').map((char, idx) => {
                        const span = (siteSettings.heroTitleColorSpans || []).find(
                          s => idx >= s.startIndex && idx < s.endIndex
                        );
                        const charColor = span ? span.color : (siteSettings.heroTitleColor || '#FFFFFF');
                        const isSelected = heroColorSelection?.includes(idx);
                        
                        return (
                          <span
                            key={idx}
                            onClick={() => {
                              // Toggle selection
                              setHeroColorSelection(prev => {
                                if (!prev) return [idx];
                                if (prev.includes(idx)) {
                                  return prev.filter(i => i !== idx);
                                }
                                return [...prev, idx].sort((a, b) => a - b);
                              });
                            }}
                            className={`cursor-pointer px-0.5 rounded transition-all ${
                              isSelected ? 'ring-2 ring-blue-500 ring-offset-1 ring-offset-gray-900' : 'hover:bg-gray-700'
                            }`}
                            style={{ 
                              color: charColor,
                              backgroundColor: isSelected ? 'rgba(59, 130, 246, 0.3)' : 'transparent'
                            }}
                          >
                            {char === '\n' ? '↵' : char === ' ' ? '\u00A0' : char}
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-gray-400 text-xs mt-2">Click characters to select, then apply a color</p>
                  </div>
                  
                  {/* Color Application Controls */}
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <input 
                        type="color"
                        value={heroApplyColor || '#FF0000'}
                        onChange={(e) => setHeroApplyColor(e.target.value)}
                        className="w-10 h-8 border rounded cursor-pointer"
                      />
                      <Input 
                        value={heroApplyColor || '#FF0000'}
                        onChange={(e) => setHeroApplyColor(e.target.value)}
                        placeholder="#FF0000"
                        className="w-28 font-mono text-sm"
                      />
                    </div>
                    <Button
                      variant="default"
                      size="sm"
                      disabled={!heroColorSelection || heroColorSelection.length === 0}
                      onClick={() => {
                        if (!heroColorSelection || heroColorSelection.length === 0) return;
                        
                        const sorted = [...heroColorSelection].sort((a, b) => a - b);
                        const startIndex = sorted[0];
                        const endIndex = sorted[sorted.length - 1] + 1;
                        
                        // Remove overlapping spans and add new one
                        const existingSpans = (siteSettings.heroTitleColorSpans || []).filter(
                          s => s.endIndex <= startIndex || s.startIndex >= endIndex
                        );
                        
                        const newSpan = { startIndex, endIndex, color: heroApplyColor || '#FF0000' };
                        const newSpans = [...existingSpans, newSpan].sort((a, b) => a.startIndex - b.startIndex);
                        
                        setSiteSettings({...siteSettings, heroTitleColorSpans: newSpans});
                        setHeroColorSelection([]);
                      }}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Apply Color to Selection
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHeroColorSelection([])}
                    >
                      Clear Selection
                    </Button>
                  </div>
                  
                  {/* Active Color Spans List */}
                  {(siteSettings.heroTitleColorSpans || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-xs text-gray-500 mb-2">Active color spans:</p>
                      <div className="flex flex-wrap gap-2">
                        {(siteSettings.heroTitleColorSpans || []).map((span, idx) => (
                          <span 
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs"
                          >
                            <span 
                              className="w-3 h-3 rounded-full border"
                              style={{ backgroundColor: span.color }}
                            />
                            &ldquo;{(localHeroTitle || '').substring(span.startIndex, span.endIndex)}&rdquo;
                            <button
                              onClick={() => {
                                const newSpans = (siteSettings.heroTitleColorSpans || []).filter((_, i) => i !== idx);
                                setSiteSettings({...siteSettings, heroTitleColorSpans: newSpans});
                              }}
                              className="ml-1 text-gray-400 hover:text-red-500"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Contrast Warning */}
                  {(siteSettings.heroTitleColorSpans || []).some(span => {
                    const hex = span.color.replace('#', '');
                    const r = parseInt(hex.substr(0, 2), 16) || 0;
                    const g = parseInt(hex.substr(2, 2), 16) || 0;
                    const b = parseInt(hex.substr(4, 2), 16) || 0;
                    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                    return luminance < 0.3;
                  }) && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Some colors may have low contrast on dark backgrounds
                    </p>
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Desktop Font Size: {siteSettings.heroTitleFontSizeDesktop || 72}px
                    </label>
                    <input 
                      type="range" 
                      min="32" 
                      max="120" 
                      value={siteSettings.heroTitleFontSizeDesktop || 72}
                      onChange={(e) => setSiteSettings({...siteSettings, heroTitleFontSizeDesktop: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Font Size: {siteSettings.heroTitleFontSizeMobile || 48}px
                    </label>
                    <input 
                      type="range" 
                      min="24" 
                      max="72" 
                      value={siteSettings.heroTitleFontSizeMobile || 48}
                      onChange={(e) => setSiteSettings({...siteSettings, heroTitleFontSizeMobile: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Default Title Color</label>
                    <div className="flex gap-2">
                      <input 
                        type="color"
                        value={siteSettings.heroTitleColor || '#FFFFFF'}
                        onChange={(e) => setSiteSettings({...siteSettings, heroTitleColor: e.target.value})}
                        className="w-12 h-10 border rounded cursor-pointer"
                      />
                      <Input 
                        value={siteSettings.heroTitleColor || '#FFFFFF'}
                        onChange={(e) => setSiteSettings({...siteSettings, heroTitleColor: e.target.value})}
                        placeholder="#FFFFFF"
                        className="flex-1 font-mono"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Used for characters without custom color</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Font Weight</label>
                    <select 
                      value={siteSettings.heroTitleFontWeight || 'bold'}
                      onChange={(e) => setSiteSettings({...siteSettings, heroTitleFontWeight: e.target.value})}
                      className="w-full border rounded-md p-2"
                    >
                      <option value="normal">Normal (400)</option>
                      <option value="medium">Medium (500)</option>
                      <option value="semibold">Semibold (600)</option>
                      <option value="bold">Bold (700)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Text Alignment</label>
                    <select 
                      value={siteSettings.heroTitleAlignment || 'center'}
                      onChange={(e) => setSiteSettings({...siteSettings, heroTitleAlignment: e.target.value})}
                      className="w-full border rounded-md p-2"
                    >
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Subtitle */}
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-4">Hero Subtitle</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle Text</label>
                  <Textarea 
                    value={localHeroSubtitle} 
                    onChange={(e) => setLocalHeroSubtitle(e.target.value)}
                    onBlur={(e) => handleBlur('heroSubtitle', e.target.value)}
                    placeholder="Supporting text under the headline"
                    rows={2}
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Desktop Font Size: {siteSettings.heroSubtitleFontSizeDesktop || 24}px
                    </label>
                    <input 
                      type="range" 
                      min="14" 
                      max="36" 
                      value={siteSettings.heroSubtitleFontSizeDesktop || 24}
                      onChange={(e) => setSiteSettings({...siteSettings, heroSubtitleFontSizeDesktop: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Font Size: {siteSettings.heroSubtitleFontSizeMobile || 18}px
                    </label>
                    <input 
                      type="range" 
                      min="12" 
                      max="28" 
                      value={siteSettings.heroSubtitleFontSizeMobile || 18}
                      onChange={(e) => setSiteSettings({...siteSettings, heroSubtitleFontSizeMobile: parseInt(e.target.value)})}
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle Color</label>
                  <div className="flex gap-2">
                    <input 
                      type="color"
                      value={siteSettings.heroSubtitleColor || '#E5E7EB'}
                      onChange={(e) => setSiteSettings({...siteSettings, heroSubtitleColor: e.target.value})}
                      className="w-12 h-10 border rounded cursor-pointer"
                    />
                    <Input 
                      value={siteSettings.heroSubtitleColor || '#E5E7EB'}
                      onChange={(e) => setSiteSettings({...siteSettings, heroSubtitleColor: e.target.value})}
                      placeholder="#E5E7EB"
                      className="w-40 font-mono"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Hero Button & Video */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hero Button Text</label>
                <Input 
                  value={siteSettings.heroButtonText || ''} 
                  onChange={(e) => setSiteSettings({...siteSettings, heroButtonText: e.target.value})}
                  placeholder="e.g., Our Services"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hero Button Link</label>
                <Input 
                  value={siteSettings.heroButtonLink || ''} 
                  onChange={(e) => setSiteSettings({...siteSettings, heroButtonLink: e.target.value})}
                  placeholder="e.g., /services or /contact"
                />
              </div>
            </div>
            <ImageUploader 
              label="Hero Background Video/Image"
              value={siteSettings.heroVideoUrl}
              onChange={(val) => setSiteSettings({...siteSettings, heroVideoUrl: val})}
              folder="Homepage"
              acceptVideo={true}
            />

            {/* Live Preview */}
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-800 text-white text-xs px-3 py-1.5">Live Preview</div>
              <div 
                className="relative h-64 bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center"
                style={{ textAlign: siteSettings.heroTitleAlignment || 'center' }}
              >
                <div className="px-4 w-full max-w-2xl">
                  {siteSettings.heroLogoEnabled && siteSettings.heroLogoUrl && (
                    <div 
                      className="mb-4"
                      style={{ 
                        textAlign: siteSettings.heroLogoAlignment || 'center',
                        marginBottom: `${siteSettings.heroLogoSpacing || 24}px`
                      }}
                    >
                      <img 
                        src={siteSettings.heroLogoUrl.startsWith('http') ? siteSettings.heroLogoUrl : `${BACKEND_URL}${siteSettings.heroLogoUrl}`}
                        alt="Hero Logo Preview"
                        style={{ 
                          height: `${Math.min((siteSettings.heroLogoSizeDesktop || 120) / 2, 60)}px`,
                          maxWidth: `${Math.min((siteSettings.heroLogoMaxWidth || 300) / 2, 150)}px`,
                          display: 'inline-block'
                        }}
                      />
                    </div>
                  )}
                  <h1 
                    style={{ 
                      fontSize: `${Math.min((siteSettings.heroTitleFontSizeDesktop || 72) / 2.5, 36)}px`,
                      fontWeight: siteSettings.heroTitleFontWeight === 'normal' ? 400 : 
                                  siteSettings.heroTitleFontWeight === 'medium' ? 500 :
                                  siteSettings.heroTitleFontWeight === 'semibold' ? 600 : 700,
                      lineHeight: 1.2,
                      marginBottom: '8px'
                    }}
                  >
                    {/* Render with per-character colors in preview */}
                    {(() => {
                      const text = localHeroTitle || 'Engineering Excellence';
                      const spans = siteSettings.heroTitleColorSpans || [];
                      const defaultColor = siteSettings.heroTitleColor || '#FFFFFF';
                      
                      if (spans.length === 0) {
                        return text.split('\n').map((line, i) => (
                          <span key={i} className={i > 0 ? 'block' : ''} style={{ color: defaultColor }}>
                            {line}
                          </span>
                        ));
                      }
                      
                      return text.split('').map((char, idx) => {
                        const span = spans.find(s => idx >= s.startIndex && idx < s.endIndex);
                        const charColor = span ? span.color : defaultColor;
                        
                        if (char === '\n') {
                          return <br key={idx} />;
                        }
                        return (
                          <span key={idx} style={{ color: charColor }}>
                            {char}
                          </span>
                        );
                      });
                    })()}
                  </h1>
                  <p 
                    style={{ 
                      fontSize: `${Math.min((siteSettings.heroSubtitleFontSizeDesktop || 24) / 2, 14)}px`,
                      color: siteSettings.heroSubtitleColor || '#E5E7EB'
                    }}
                  >
                    {localHeroSubtitle || 'Professional MEP Engineering & Design Consultancy'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Featured Highlight Section */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5" /> Featured Highlight Section
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
              <span className="font-medium">Show Featured Highlight on Homepage</span>
              <button
                onClick={() => {
                  const fh = siteSettings.featuredHighlight || {};
                  setSiteSettings({
                    ...siteSettings, 
                    featuredHighlight: {...fh, showSection: fh.showSection === false ? true : false}
                  });
                }}
                className={`p-2 rounded-lg transition-colors ${
                  siteSettings.featuredHighlight?.showSection !== false 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {siteSettings.featuredHighlight?.showSection !== false ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Badge/Subtitle</label>
              <Input 
                value={siteSettings.featuredHighlight?.subtitle || 'Why Choose FURD'} 
                onChange={(e) => setSiteSettings({
                  ...siteSettings, 
                  featuredHighlight: {...(siteSettings.featuredHighlight || {}), subtitle: e.target.value}
                })}
                placeholder="e.g., Why Choose FURD"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Main Title</label>
              <Input 
                value={siteSettings.featuredHighlight?.title || ''} 
                onChange={(e) => setSiteSettings({
                  ...siteSettings, 
                  featuredHighlight: {...(siteSettings.featuredHighlight || {}), title: e.target.value}
                })}
                placeholder="Engineering solutions that work harder..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea 
                value={siteSettings.featuredHighlight?.description || ''} 
                onChange={(e) => setSiteSettings({
                  ...siteSettings, 
                  featuredHighlight: {...(siteSettings.featuredHighlight || {}), description: e.target.value}
                })}
                placeholder="We combine technical excellence with practical buildability..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Text</label>
                <Input 
                  value={siteSettings.featuredHighlight?.primaryButtonText || 'Explore Our Services'} 
                  onChange={(e) => setSiteSettings({
                    ...siteSettings, 
                    featuredHighlight: {...(siteSettings.featuredHighlight || {}), primaryButtonText: e.target.value}
                  })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Button Link</label>
                <Input 
                  value={siteSettings.featuredHighlight?.primaryButtonLink || '/services'} 
                  onChange={(e) => setSiteSettings({
                    ...siteSettings, 
                    featuredHighlight: {...(siteSettings.featuredHighlight || {}), primaryButtonLink: e.target.value}
                  })}
                  placeholder="/services or https://..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Text</label>
                <Input 
                  value={siteSettings.featuredHighlight?.secondaryButtonText || 'Get in Touch'} 
                  onChange={(e) => setSiteSettings({
                    ...siteSettings, 
                    featuredHighlight: {...(siteSettings.featuredHighlight || {}), secondaryButtonText: e.target.value}
                  })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Secondary Button Link</label>
                <Input 
                  value={siteSettings.featuredHighlight?.secondaryButtonLink || '/contact'} 
                  onChange={(e) => setSiteSettings({
                    ...siteSettings, 
                    featuredHighlight: {...(siteSettings.featuredHighlight || {}), secondaryButtonLink: e.target.value}
                  })}
                  placeholder="/contact or https://..."
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About Section */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle>About Section</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">About Description</label>
              <Textarea 
                value={localAboutDescription} 
                onChange={(e) => setLocalAboutDescription(e.target.value)}
                onBlur={(e) => handleBlur('aboutDescription', e.target.value)}
                rows={4}
              />
            </div>
            <ImageUploader 
              label="About Section Image"
              value={siteSettings.aboutImage}
              onChange={(val) => setSiteSettings({...siteSettings, aboutImage: val})}
              folder="Homepage"
            />
          </CardContent>
        </Card>

        {/* Homepage Sections Visibility */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" /> Section Visibility
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-sm text-gray-500 mb-4">Toggle which sections appear on your homepage</p>
            <div className="space-y-3">
              {(siteSettings.homepageSections || []).map((section, idx) => (
                <div key={section.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <GripVertical className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{section.title}</span>
                  </div>
                  <button
                    onClick={() => {
                      const updated = [...(siteSettings.homepageSections || [])];
                      updated[idx].visible = !updated[idx].visible;
                      setSiteSettings({...siteSettings, homepageSections: updated});
                    }}
                    className={`p-2 rounded-lg transition-colors ${section.visible ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                  >
                    {section.visible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSettingsSave} size="lg" className="w-full bg-green-600 hover:bg-green-700">
          <Save className="w-5 h-5 mr-2" /> Save Homepage Changes
        </Button>
      </div>
    );
  };

  // ==================== GENERIC ITEM EDITOR ====================
  const ItemEditor = ({ item, type, onSave, onCancel }) => {
    // Initialize with default values for select fields
    const getInitialItem = () => {
      const initialItem = {...item};
      
      // Set default values for jobs
      if (type === 'jobs' && !initialItem.id) {
        initialItem.type = initialItem.type || 'Full-time';
        initialItem.isActive = initialItem.isActive !== false;
      }
      
      // Set default values for services
      if (type === 'services' && !initialItem.id) {
        initialItem.category = initialItem.category || 'design';
      }
      
      return initialItem;
    };
    
    const [editedItem, setEditedItem] = useState(getInitialItem());

    // Map type to folder for image organization
    const typeToFolder = {
      services: 'Services',
      sectors: 'Sectors', 
      projects: 'Projects',
      team: 'Team',
      jobs: 'Other'
    };

    const fields = {
      services: [
        { key: 'title', label: 'Service Title', type: 'text' },
        { key: 'description', label: 'Short Description', type: 'textarea' },
        { key: 'detailedDescription', label: 'Detailed Description', type: 'textarea', rows: 4 },
        { key: 'image', label: 'Service Image', type: 'image', folder: 'Services' },
        { key: 'category', label: 'Category', type: 'select', options: ['design', 'consultancy'] },
      ],
      sectors: [
        { key: 'title', label: 'Sector Title', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea', rows: 4 },
        { key: 'image', label: 'Sector Image', type: 'image', folder: 'Sectors' },
        { key: 'icon', label: 'Icon Name', type: 'text', placeholder: 'e.g., Building2, ShoppingBag, Home' },
        { key: 'features', label: 'Our Expertise (one per line)', type: 'list', rows: 5 },
        { key: 'projectTypes', label: 'Project Types (one per line)', type: 'list', rows: 5 },
        { key: 'order', label: 'Display Order', type: 'number' },
        { key: 'isVisible', label: 'Visible on Site', type: 'checkbox' },
      ],
      projects: [
        { key: 'name', label: 'Project Name', type: 'text' },
        { key: 'location', label: 'Location', type: 'text' },
        { key: 'client', label: 'Client', type: 'text' },
        { key: 'description', label: 'Description', type: 'textarea', rows: 3 },
        { key: 'sectorId', label: 'Sector Category', type: 'sector-select' },
        { key: 'category', label: 'Category (Legacy)', type: 'text', placeholder: 'e.g., Retail, Commercial' },
        { key: 'image', label: 'Project Image', type: 'image', folder: 'Projects' },
        { key: 'featured', label: 'Featured Project', type: 'checkbox' },
      ],
      team: [
        { key: 'name', label: 'Full Name', type: 'text' },
        { key: 'position', label: 'Job Title', type: 'text' },
        { key: 'discipline', label: 'Discipline/Specialism', type: 'text', placeholder: 'e.g., Mechanical, Electrical, Public Health' },
        { key: 'officeLocation', label: 'Office Location', type: 'text', placeholder: 'e.g., London' },
        { key: 'image', label: 'Profile Photo', type: 'image', folder: 'Team' },
        { key: 'photoPosition', label: 'Photo Crop Position', type: 'photoPosition', imageKey: 'image' },
        { key: 'shortBio', label: 'Short Bio (for card preview)', type: 'textarea', rows: 2 },
        { key: 'fullBio', label: 'Full Biography (for profile page)', type: 'textarea', rows: 5 },
        { key: 'keyExperience', label: 'Key Experience (one per line)', type: 'list' },
        { key: 'qualifications', label: 'Qualifications (one per line)', type: 'list' },
        { key: 'memberships', label: 'Professional Memberships (one per line)', type: 'list' },
        { key: 'projectsWorkedOn', label: 'Notable Projects (one per line)', type: 'list' },
        { key: 'email', label: 'Email', type: 'text' },
        { key: 'linkedin', label: 'LinkedIn URL', type: 'text' },
        { key: 'order', label: 'Display Order', type: 'number' },
        { key: 'isFeatured', label: 'Featured on Homepage', type: 'checkbox' },
        { key: 'isPublished', label: 'Published (visible on site)', type: 'checkbox' },
      ],
      jobs: [
        { key: 'title', label: 'Job Title', type: 'text' },
        { key: 'type', label: 'Employment Type', type: 'select', options: ['Full-time', 'Part-time', 'Contract'] },
        { key: 'location', label: 'Location', type: 'text' },
        { key: 'description', label: 'Job Description', type: 'textarea', rows: 4 },
        { key: 'salary', label: 'Salary Range', type: 'text', placeholder: 'e.g., £35,000 - £50,000' },
        { key: 'isActive', label: 'Active (visible)', type: 'checkbox' },
      ],
    };

    const currentFields = fields[type] || [];

    return (
      <Card className="border-2 border-blue-500 shadow-xl">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center justify-between">
            <span>{item.id ? 'Edit' : 'Add New'} {type === 'team' ? 'Team Member' : type.slice(0, -1)}</span>
            <Button variant="ghost" size="sm" onClick={onCancel}><X className="w-4 h-4" /></Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          {currentFields.map((field) => (
            <div key={field.key}>
              {field.type === 'text' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  <Input 
                    value={editedItem[field.key] || ''} 
                    onChange={(e) => setEditedItem({...editedItem, [field.key]: e.target.value})}
                    placeholder={field.placeholder || ''}
                  />
                </>
              )}
              {field.type === 'number' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  <Input 
                    type="number"
                    value={editedItem[field.key] || 0} 
                    onChange={(e) => setEditedItem({...editedItem, [field.key]: parseInt(e.target.value) || 0})}
                  />
                </>
              )}
              {field.type === 'textarea' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  <Textarea 
                    value={editedItem[field.key] || ''} 
                    onChange={(e) => setEditedItem({...editedItem, [field.key]: e.target.value})}
                    rows={field.rows || 3}
                  />
                </>
              )}
              {field.type === 'select' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={editedItem[field.key] || field.options[0]} 
                    onChange={(e) => setEditedItem({...editedItem, [field.key]: e.target.value})}
                  >
                    {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                </>
              )}
              {field.type === 'sector-select' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  <select 
                    className="w-full border rounded-md p-2 bg-white"
                    value={editedItem[field.key] || ''} 
                    onChange={(e) => setEditedItem({...editedItem, [field.key]: e.target.value})}
                  >
                    <option value="">-- Select Sector --</option>
                    {sectors.map(sector => (
                      <option key={sector.id} value={sector.id}>{sector.title}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {editedItem[field.key] ? 
                      `Selected: ${sectors.find(s => s.id === editedItem[field.key])?.title || 'Unknown'}` : 
                      'No sector selected'}
                  </p>
                </>
              )}
              {field.type === 'image' && (
                <ImageUploader 
                  label={field.label}
                  value={editedItem[field.key]}
                  onChange={(val) => setEditedItem({...editedItem, [field.key]: val})}
                  folder={field.folder || 'Other'}
                />
              )}
              {field.type === 'checkbox' && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox"
                    checked={editedItem[field.key] || false}
                    onChange={(e) => setEditedItem({...editedItem, [field.key]: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium text-gray-700">{field.label}</span>
                </label>
              )}
              {field.type === 'list' && (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                  <Textarea 
                    value={(editedItem[field.key] || []).join('\n')} 
                    onChange={(e) => setEditedItem({
                      ...editedItem, 
                      [field.key]: e.target.value.split('\n')
                    })}
                    onKeyDown={(e) => {
                      // Allow Enter key to create new lines without submitting form
                      if (e.key === 'Enter') {
                        e.stopPropagation();
                      }
                    }}
                    rows={field.rows || 5}
                    placeholder="Enter items, one per line. Press Enter to add a new line."
                    className="font-mono text-sm resize-y min-h-[120px]"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {(editedItem[field.key] || []).filter(line => line.trim()).length} items 
                    <span className="text-gray-400 ml-2">• Press Enter to add new lines</span>
                  </p>
                </>
              )}
              {field.type === 'photoPosition' && editedItem[field.imageKey] && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">{field.label}</label>
                  <p className="text-xs text-gray-500">Adjust the slider to position where the photo crops. Lower values show more of the top (head), higher values show more of the bottom.</p>
                  
                  {/* Preview with crop indicator */}
                  <div className="flex gap-4 items-start">
                    <div className="w-32 h-40 rounded-lg overflow-hidden border-2 border-blue-500 relative flex-shrink-0 bg-gray-100">
                      <img 
                        src={editedItem[field.imageKey]} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                        style={{ objectPosition: `center ${editedItem[field.key] || 20}%` }}
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-blue-600 text-white text-xs text-center py-1 font-medium">
                        Preview
                      </div>
                    </div>
                    
                    <div className="flex-1 space-y-3">
                      {/* Slider */}
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-12">Top</span>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={editedItem[field.key] || 20}
                          onChange={(e) => setEditedItem({...editedItem, [field.key]: parseInt(e.target.value)})}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        <span className="text-xs text-gray-500 w-12 text-right">Bottom</span>
                      </div>
                      
                      {/* Quick presets */}
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setEditedItem({...editedItem, [field.key]: 0})}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${editedItem[field.key] === 0 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                        >
                          Top (Head)
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditedItem({...editedItem, [field.key]: 20})}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${editedItem[field.key] === 20 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                        >
                          Face Focus
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditedItem({...editedItem, [field.key]: 50})}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${editedItem[field.key] === 50 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                        >
                          Center
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditedItem({...editedItem, [field.key]: 75})}
                          className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${editedItem[field.key] === 75 ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}
                        >
                          Lower
                        </button>
                      </div>
                      
                      <p className="text-xs text-gray-400">Current position: {editedItem[field.key] || 20}%</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          <div className="flex gap-2 pt-4">
            <Button onClick={() => onSave(editedItem)} className="bg-green-600 hover:bg-green-700 flex-1">
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ==================== GENERIC LIST VIEW ====================
  const ItemList = ({ items, type, titleKey, subtitleKey, imageKey, onEdit, onDelete }) => {
    // Add cache-busting based on updatedAt timestamp
    const getImageUrl = (item) => {
      if (!imageKey || !item[imageKey]) return null;
      const baseUrl = item[imageKey];
      // Add timestamp for cache busting if item has updatedAt
      if (item.updatedAt) {
        const separator = baseUrl.includes('?') ? '&' : '?';
        return `${baseUrl}${separator}v=${new Date(item.updatedAt).getTime()}`;
      }
      return baseUrl;
    };

    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <Card key={item.id || item._id} className="border-0 shadow-lg hover:shadow-xl transition-shadow overflow-hidden">
            {imageKey && item[imageKey] && (
              <div className="h-40 overflow-hidden">
                <img 
                  src={getImageUrl(item)} 
                  alt={item[titleKey]} 
                  className="w-full h-full object-cover"
                  key={`img-${item.id}-${item.updatedAt}`}
                />
              </div>
            )}
            <CardContent className="p-4">
              <h3 className="font-bold text-lg text-gray-900 mb-1">{item[titleKey]}</h3>
              {subtitleKey && <p className="text-gray-500 text-sm mb-3 line-clamp-2">{item[subtitleKey]}</p>}
              <div className="flex gap-2">
                <Button size="sm" onClick={() => onEdit(item)} className="bg-blue-600 hover:bg-blue-700 flex-1">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" variant="outline" onClick={() => onDelete(item.id, item[titleKey])} className="border-red-500 text-red-500 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  // ==================== CONTENT TAB ====================
  const ContentTab = ({ type, items, titleKey, subtitleKey, imageKey, addLabel }) => {
    const isEditing = editingType === type && editingItem;
    const isAdding = newFormType === type && showNewForm;

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Manage {type.charAt(0).toUpperCase() + type.slice(1)}</h2>
          <Button 
            onClick={() => { setNewFormType(type); setShowNewForm(true); setEditingItem({}); }}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" /> Add {addLabel || type.slice(0, -1)}
          </Button>
        </div>

        {(isEditing || isAdding) && (
          <ItemEditor 
            item={editingItem}
            type={type}
            onSave={(item) => handleSave(type, item, !item.id)}
            onCancel={() => { setEditingItem(null); setEditingType(null); setShowNewForm(false); setNewFormType(null); }}
          />
        )}

        {!isEditing && !isAdding && (
          <ItemList 
            items={items}
            type={type}
            titleKey={titleKey}
            subtitleKey={subtitleKey}
            imageKey={imageKey}
            onEdit={(item) => { setEditingItem(item); setEditingType(type); }}
            onDelete={(id, name) => handleDelete(type, id, name)}
          />
        )}

        {items.length === 0 && !isAdding && (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No {type} yet. Click &quot;Add&quot; to create one.</p>
          </div>
        )}
      </div>
    );
  };

  // ==================== SERVICES OVERVIEW TAB ====================
  const ServicesOverviewTab = () => {
    const [localSettings, setLocalSettings] = useState({
      sectionTitle: 'Our Services',
      sectionIntro: 'Comprehensive MEP engineering and design consultancy services.',
      showSection: true,
      displayStyle: 'cards',
      serviceItems: [],
      designServicesTitle: 'Design Services',
      designServicesSubtitle: 'Engineering Excellence',
      showDesignServices: true,
      consultancyServicesTitle: 'Consultancy Services',
      consultancyServicesSubtitle: 'Expert Guidance',
      showConsultancyServices: true,
      viewAllText: 'View All Services',
      viewAllLink: '/services',
      showViewAllLink: true
    });
    const [saving, setSaving] = useState(false);
    const [editingItemIndex, setEditingItemIndex] = useState(null);

    // Fetch settings on mount
    useEffect(() => {
      const fetchSettings = async () => {
        try {
          const res = await axios.get(`${API}/services-overview-settings`);
          if (res.data.servicesOverviewSettings) {
            setLocalSettings(prev => ({ ...prev, ...res.data.servicesOverviewSettings }));
          }
        } catch (err) {
          console.error('Error fetching services overview settings:', err);
        }
      };
      fetchSettings();
    }, []);

    const handleSave = async () => {
      setSaving(true);
      try {
        await axios.put(`${API}/services-overview-settings`, localSettings);
        toast({ title: "Services Overview settings saved!", description: "Changes will appear on the frontend." });
        fetchAllData();
      } catch (err) {
        toast({ title: "Error saving", description: err.message, variant: "destructive" });
      } finally {
        setSaving(false);
      }
    };

    const addServiceItem = () => {
      const newItem = {
        id: `service-${Date.now()}`,
        name: '',
        shortDescription: '',
        icon: '',
        image: '',
        learnMoreText: 'Learn More',
        learnMoreTarget: '',
        isHighlighted: false,
        order: localSettings.serviceItems.length,
        isVisible: true
      };
      setLocalSettings(prev => ({
        ...prev,
        serviceItems: [...prev.serviceItems, newItem]
      }));
      setEditingItemIndex(localSettings.serviceItems.length);
    };

    const updateServiceItem = (index, updates) => {
      setLocalSettings(prev => ({
        ...prev,
        serviceItems: prev.serviceItems.map((item, i) => 
          i === index ? { ...item, ...updates } : item
        )
      }));
    };

    const deleteServiceItem = (index) => {
      if (window.confirm('Delete this service item?')) {
        setLocalSettings(prev => ({
          ...prev,
          serviceItems: prev.serviceItems.filter((_, i) => i !== index)
        }));
        setEditingItemIndex(null);
      }
    };

    const moveServiceItem = (index, direction) => {
      const items = [...localSettings.serviceItems];
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= items.length) return;
      [items[index], items[newIndex]] = [items[newIndex], items[index]];
      items.forEach((item, i) => item.order = i);
      setLocalSettings(prev => ({ ...prev, serviceItems: items }));
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Services Overview</h2>
            <p className="text-gray-600">Configure the Services Overview section on the Homepage</p>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save & Publish
          </Button>
        </div>

        {/* Global Section Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" /> Global Section Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={localSettings.showSection}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, showSection: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm font-medium">Show Services Section</span>
              </label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                <Input
                  value={localSettings.sectionTitle}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, sectionTitle: e.target.value }))}
                  placeholder="Our Services"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Style</label>
                <select
                  value={localSettings.displayStyle}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, displayStyle: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="cards">Cards</option>
                  <option value="list">List</option>
                  <option value="grid">Grid</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Intro Text</label>
              <Textarea
                value={localSettings.sectionIntro}
                onChange={(e) => setLocalSettings(prev => ({ ...prev, sectionIntro: e.target.value }))}
                placeholder="Comprehensive MEP engineering services..."
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Design Services Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Design Services Section
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={localSettings.showDesignServices}
                onChange={(e) => setLocalSettings(prev => ({ ...prev, showDesignServices: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm font-medium">Show Design Services</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <Input
                  value={localSettings.designServicesTitle}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, designServicesTitle: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                <Input
                  value={localSettings.designServicesSubtitle}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, designServicesSubtitle: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Consultancy Services Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="w-5 h-5" /> Consultancy Services Section
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={localSettings.showConsultancyServices}
                onChange={(e) => setLocalSettings(prev => ({ ...prev, showConsultancyServices: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm font-medium">Show Consultancy Services</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <Input
                  value={localSettings.consultancyServicesTitle}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, consultancyServicesTitle: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                <Input
                  value={localSettings.consultancyServicesSubtitle}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, consultancyServicesSubtitle: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* View All Link */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link className="w-5 h-5" /> View All Link
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={localSettings.showViewAllLink}
                onChange={(e) => setLocalSettings(prev => ({ ...prev, showViewAllLink: e.target.checked }))}
                className="rounded"
              />
              <span className="text-sm font-medium">Show "View All" Link</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                <Input
                  value={localSettings.viewAllText}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, viewAllText: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link Target</label>
                <Input
                  value={localSettings.viewAllLink}
                  onChange={(e) => setLocalSettings(prev => ({ ...prev, viewAllLink: e.target.value }))}
                  placeholder="/services"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Custom Service Items */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Wrench className="w-5 h-5" /> Custom Service Items
              </span>
              <Button onClick={addServiceItem} size="sm">
                <Plus className="w-4 h-4 mr-1" /> Add Service
              </Button>
            </CardTitle>
            <p className="text-sm text-gray-500">Add custom service items to display in the overview section</p>
          </CardHeader>
          <CardContent>
            {localSettings.serviceItems.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No custom service items yet.</p>
                <p className="text-sm text-gray-400">The section will use services from the Services database.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {localSettings.serviceItems.map((item, index) => (
                  <div key={item.id} className="border rounded-lg p-4 bg-white">
                    {editingItemIndex === index ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
                            <Input
                              value={item.name}
                              onChange={(e) => updateServiceItem(index, { name: e.target.value })}
                              placeholder="Service name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Icon (lucide-react)</label>
                            <Input
                              value={item.icon}
                              onChange={(e) => updateServiceItem(index, { icon: e.target.value })}
                              placeholder="Building2, Wrench, etc."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Short Description *</label>
                          <Textarea
                            value={item.shortDescription}
                            onChange={(e) => updateServiceItem(index, { shortDescription: e.target.value })}
                            placeholder="Brief description for the overview card"
                            rows={2}
                          />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Learn More Text</label>
                            <Input
                              value={item.learnMoreText}
                              onChange={(e) => updateServiceItem(index, { learnMoreText: e.target.value })}
                              placeholder="Learn More"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Learn More Link</label>
                            <Input
                              value={item.learnMoreTarget}
                              onChange={(e) => updateServiceItem(index, { learnMoreTarget: e.target.value })}
                              placeholder="/services/mechanical or https://..."
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Image URL (optional)</label>
                          <Input
                            value={item.image}
                            onChange={(e) => updateServiceItem(index, { image: e.target.value })}
                            placeholder="Select from image library or paste URL"
                          />
                        </div>
                        <div className="flex items-center gap-4">
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={item.isHighlighted}
                              onChange={(e) => updateServiceItem(index, { isHighlighted: e.target.checked })}
                              className="rounded"
                            />
                            <span className="text-sm">Featured Service</span>
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={item.isVisible}
                              onChange={(e) => updateServiceItem(index, { isVisible: e.target.checked })}
                              className="rounded"
                            />
                            <span className="text-sm">Visible</span>
                          </label>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => setEditingItemIndex(null)}>
                            Done
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => deleteServiceItem(index)}>
                            <Trash2 className="w-4 h-4 mr-1" /> Delete
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex flex-col gap-1">
                            <button onClick={() => moveServiceItem(index, 'up')} disabled={index === 0} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <button onClick={() => moveServiceItem(index, 'down')} disabled={index === localSettings.serviceItems.length - 1} className="text-gray-400 hover:text-gray-600 disabled:opacity-30">
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">{item.name || 'Untitled Service'}</h4>
                            <p className="text-sm text-gray-500 truncate max-w-md">{item.shortDescription || 'No description'}</p>
                          </div>
                          {item.isHighlighted && (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Featured</span>
                          )}
                          {!item.isVisible && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">Hidden</span>
                          )}
                        </div>
                        <Button variant="outline" size="sm" onClick={() => setEditingItemIndex(index)}>
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // ==================== SITE SETTINGS TAB ====================
  const SiteSettingsTab = () => {
    // Local state for values editing
    const [localValues, setLocalValues] = useState(siteSettings.values || []);
    const [editingValueIndex, setEditingValueIndex] = useState(null);
    
    // Update local values when siteSettings changes
    React.useEffect(() => {
      setLocalValues(siteSettings.values || []);
    }, [siteSettings.values]);
    
    const handleAddValue = () => {
      const newValue = {
        name: '',
        description: '',
        meaning: '',
        importance: '',
        approach: '',
        learnMoreText: 'Explore this value',
        learnMoreUrl: ''
      };
      setLocalValues([...localValues, newValue]);
      setEditingValueIndex(localValues.length);
    };
    
    const handleRemoveValue = (index) => {
      setLocalValues(localValues.filter((_, i) => i !== index));
      setEditingValueIndex(null);
    };
    
    const handleValueChange = (index, field, value) => {
      const updated = [...localValues];
      updated[index] = { ...updated[index], [field]: value };
      setLocalValues(updated);
    };
    
    const handleSaveValues = async () => {
      try {
        // Update siteSettings with new values
        const updatedSettings = { ...siteSettings, values: localValues };
        await axios.put(`${API}/settings`, updatedSettings);
        setSiteSettings(updatedSettings);
        toast({ title: "Saved!", description: "Values updated successfully" });
        setEditingValueIndex(null);
      } catch (error) {
        toast({ title: "Error", description: "Failed to save values", variant: "destructive" });
      }
    };
    
    return (
    <div className="space-y-6">
      {/* Company Information Card */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gray-50">
          <CardTitle>Company Information</CardTitle>
        </CardHeader>
        <CardContent className="p-6 grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
            <Input value={siteSettings.companyName || ''} onChange={(e) => setSiteSettings({...siteSettings, companyName: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tagline</label>
            <Input value={siteSettings.tagline || ''} onChange={(e) => setSiteSettings({...siteSettings, tagline: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <Input value={siteSettings.phone || ''} onChange={(e) => setSiteSettings({...siteSettings, phone: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input value={siteSettings.email || ''} onChange={(e) => setSiteSettings({...siteSettings, email: e.target.value})} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <Input value={siteSettings.address || ''} onChange={(e) => setSiteSettings({...siteSettings, address: e.target.value})} />
          </div>
        </CardContent>
      </Card>

      {/* VALUES EDITOR - Full "Learn More" Control */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-blue-50">
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-blue-600" /> Our Values (Learn More Content)
            </span>
            <Button size="sm" onClick={handleAddValue}>
              <Plus className="w-4 h-4 mr-1" /> Add Value
            </Button>
          </CardTitle>
          <p className="text-sm text-gray-600">Edit the expanded content that appears when users click "Learn More" on each value.</p>
        </CardHeader>
        <CardContent className="p-6">
          {localValues.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No values defined. Click "Add Value" to create one.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {localValues.map((val, index) => (
                <div key={index} className={`border rounded-lg overflow-hidden ${editingValueIndex === index ? 'border-blue-500 shadow-md' : 'border-gray-200'}`}>
                  <div 
                    className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer"
                    onClick={() => setEditingValueIndex(editingValueIndex === index ? null : index)}
                  >
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-blue-600" />
                      <div>
                        <div className="font-medium">{val.name || 'Untitled Value'}</div>
                        <div className="text-sm text-gray-500">{val.description?.substring(0, 60) || 'No description'}...</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <ChevronDown className={`w-5 h-5 transition-transform ${editingValueIndex === index ? 'rotate-180' : ''}`} />
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-600 hover:text-red-700"
                        onClick={(e) => { e.stopPropagation(); handleRemoveValue(index); }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {editingValueIndex === index && (
                    <div className="p-4 space-y-4 bg-white">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Value Name *</label>
                          <Input 
                            value={val.name || ''} 
                            onChange={(e) => handleValueChange(index, 'name', e.target.value)}
                            placeholder="e.g., Collaboration"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Learn More Button Text</label>
                          <Input 
                            value={val.learnMoreText || ''} 
                            onChange={(e) => handleValueChange(index, 'learnMoreText', e.target.value)}
                            placeholder="Explore this value"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Short Description (shown on card)</label>
                        <Textarea 
                          value={val.description || ''} 
                          onChange={(e) => handleValueChange(index, 'description', e.target.value)}
                          placeholder="Brief description shown on the value card"
                          rows={2}
                        />
                      </div>
                      
                      <div className="border-t pt-4 mt-4">
                        <p className="text-sm font-semibold text-blue-600 mb-3">Expanded Content (shown in modal when clicked)</p>
                        
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">What This Means</label>
                            <Textarea 
                              value={val.meaning || ''} 
                              onChange={(e) => handleValueChange(index, 'meaning', e.target.value)}
                              placeholder="Explain what this value means in detail..."
                              rows={3}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Why It Matters in BSE</label>
                            <Textarea 
                              value={val.importance || ''} 
                              onChange={(e) => handleValueChange(index, 'importance', e.target.value)}
                              placeholder="Explain why this value is important in Building Services Engineering..."
                              rows={3}
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Our Approach</label>
                            <Textarea 
                              value={val.approach || ''} 
                              onChange={(e) => handleValueChange(index, 'approach', e.target.value)}
                              placeholder="Describe how your company applies this value..."
                              rows={3}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">External URL (optional - links instead of modal)</label>
                        <Input 
                          value={val.learnMoreUrl || ''} 
                          onChange={(e) => handleValueChange(index, 'learnMoreUrl', e.target.value)}
                          placeholder="https://... (leave empty to use modal)"
                        />
                        <p className="text-xs text-gray-500 mt-1">If set, clicking "Learn More" will open this URL instead of the expanded modal.</p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              <Button onClick={handleSaveValues} className="w-full bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" /> Save Values
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gray-50">
          <CardTitle>Social Media</CardTitle>
        </CardHeader>
        <CardContent className="p-6 grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn</label>
            <Input value={siteSettings.linkedinUrl || ''} onChange={(e) => setSiteSettings({...siteSettings, linkedinUrl: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Twitter</label>
            <Input value={siteSettings.twitterUrl || ''} onChange={(e) => setSiteSettings({...siteSettings, twitterUrl: e.target.value})} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Facebook</label>
            <Input value={siteSettings.facebookUrl || ''} onChange={(e) => setSiteSettings({...siteSettings, facebookUrl: e.target.value})} />
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSettingsSave} size="lg" className="w-full bg-green-600 hover:bg-green-700">
        <Save className="w-5 h-5 mr-2" /> Save All Settings
      </Button>
    </div>
  );
  };

  // ==================== ABOUT PAGE TAB ====================
  const AboutPageTab = () => {
    const [aboutSettings, setAboutSettings] = useState({
      metaTitle: siteSettings.aboutPage?.metaTitle || 'About Us - FURD Ltd',
      metaDescription: siteSettings.aboutPage?.metaDescription || 'Learn about FURD Ltd, a leading MEP engineering consultancy',
      heroTitle: siteSettings.aboutPage?.heroTitle || `About ${siteSettings.companyName || 'FURD'}`,
      heroSubtitle: companyInfo?.tagline || siteSettings.aboutPage?.heroSubtitle || 'Building our future by engineering projects with generational value',
      heroImage: siteSettings.aboutPage?.heroImage || '',
      aboutDescription: siteSettings.aboutDescription || '',
      vision: siteSettings.vision || '',
      mission: siteSettings.mission || '',
      aboutImage: siteSettings.aboutImage || '',
      accreditationsTitle: siteSettings.aboutPage?.accreditationsTitle || 'Memberships & Accreditations',
      accreditationsSubtitle: siteSettings.aboutPage?.accreditationsSubtitle || 'Certified excellence in MEP engineering'
    });

    const [isSaving, setIsSaving] = useState(false);
    const [lastSaved, setLastSaved] = useState(null);

    const handleSaveAboutPage = async () => {
      setIsSaving(true);
      try {
        const updatedSettings = {
          ...siteSettings,
          aboutDescription: aboutSettings.aboutDescription,
          vision: aboutSettings.vision,
          mission: aboutSettings.mission,
          aboutImage: aboutSettings.aboutImage,
          aboutPage: {
            metaTitle: aboutSettings.metaTitle,
            metaDescription: aboutSettings.metaDescription,
            heroTitle: aboutSettings.heroTitle,
            heroSubtitle: aboutSettings.heroSubtitle,
            heroImage: aboutSettings.heroImage,
            accreditationsTitle: aboutSettings.accreditationsTitle,
            accreditationsSubtitle: aboutSettings.accreditationsSubtitle
          }
        };
        
        await axios.put(`${API}/settings`, updatedSettings);
        setSiteSettings(updatedSettings);
        setLastSaved(new Date().toLocaleTimeString());
        toast({ title: "Saved", description: "About page content updated successfully" });
      } catch (error) {
        toast({ title: "Error", description: "Failed to save About page", variant: "destructive" });
      } finally {
        setIsSaving(false);
      }
    };

    return (
      <div className="space-y-6">
        {/* Status Banner */}
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <div>
              <p className="font-medium text-green-800">Connected to Frontend</p>
              <p className="text-sm text-green-600">Changes will appear on the About page after saving</p>
            </div>
          </div>
          {lastSaved && (
            <span className="text-sm text-green-600">Last saved: {lastSaved}</span>
          )}
        </div>

        {/* SEO Settings */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" /> SEO Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
              <Input 
                value={aboutSettings.metaTitle}
                onChange={(e) => setAboutSettings({...aboutSettings, metaTitle: e.target.value})}
                placeholder="About Us - Company Name"
              />
              <p className="text-xs text-gray-400 mt-1">Displayed in browser tab and search results</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
              <Textarea 
                value={aboutSettings.metaDescription}
                onChange={(e) => setAboutSettings({...aboutSettings, metaDescription: e.target.value})}
                placeholder="Brief description for search engines..."
                rows={2}
              />
              <p className="text-xs text-gray-400 mt-1">Recommended: 150-160 characters</p>
            </div>
          </CardContent>
        </Card>

        {/* Hero Section */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" /> Hero Section
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
              <Input 
                value={aboutSettings.heroTitle}
                onChange={(e) => setAboutSettings({...aboutSettings, heroTitle: e.target.value})}
                placeholder="About FURD"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle / Tagline</label>
              <Input 
                value={aboutSettings.heroSubtitle}
                onChange={(e) => setAboutSettings({...aboutSettings, heroSubtitle: e.target.value})}
                placeholder="Building our future..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Company Overview */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Company Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">About Description</label>
              <Textarea 
                value={aboutSettings.aboutDescription}
                onChange={(e) => setAboutSettings({...aboutSettings, aboutDescription: e.target.value})}
                placeholder="Describe your company history and services..."
                rows={5}
              />
              <p className="text-xs text-gray-400 mt-1">Main company description shown in "Who We Are" section</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Image</label>
              <ImageUploader 
                value={aboutSettings.aboutImage}
                onChange={(url) => setAboutSettings({...aboutSettings, aboutImage: url})}
                label="About Image"
                folder="About"
              />
              <p className="text-xs text-gray-400 mt-1">Displayed alongside the company description</p>
            </div>
          </CardContent>
        </Card>

        {/* Vision & Mission */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5" /> Vision & Mission
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vision Statement</label>
              <Textarea 
                value={aboutSettings.vision}
                onChange={(e) => setAboutSettings({...aboutSettings, vision: e.target.value})}
                placeholder="Our vision is to..."
                rows={3}
              />
              <p className="text-xs text-gray-400 mt-1">Displayed as a highlighted quote</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mission Statement</label>
              <Textarea 
                value={aboutSettings.mission}
                onChange={(e) => setAboutSettings({...aboutSettings, mission: e.target.value})}
                placeholder="Our mission is to..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Accreditations Section Settings */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5" /> Accreditations Section
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
              <Input 
                value={aboutSettings.accreditationsTitle}
                onChange={(e) => setAboutSettings({...aboutSettings, accreditationsTitle: e.target.value})}
                placeholder="Memberships & Accreditations"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section Subtitle</label>
              <Input 
                value={aboutSettings.accreditationsSubtitle}
                onChange={(e) => setAboutSettings({...aboutSettings, accreditationsSubtitle: e.target.value})}
                placeholder="Certified excellence in MEP engineering"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Accreditations content is managed in the{' '}
                <button 
                  onClick={() => setActiveTab('accreditations')}
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  Accreditations tab
                </button>
                . Enable "Show on About Page" for each accreditation you want displayed.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Values Reference */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5" /> Values Section
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Values content is managed in the{' '}
                <button 
                  onClick={() => setActiveTab('settings')}
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  Site Settings → Values tab
                </button>
                . Changes there will automatically appear on both the Homepage and About page.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="sticky bottom-4 flex justify-end">
          <Button 
            onClick={handleSaveAboutPage}
            disabled={isSaving}
            className="bg-blue-600 hover:bg-blue-700 px-8 py-3 shadow-lg"
          >
            {isSaving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save About Page
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // ==================== PAGES TAB ====================
  const PagesTab = () => (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gray-50">
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" /> Page Visibility
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-sm text-gray-500 mb-4">Control which pages appear in navigation</p>
          <div className="space-y-3">
            {pages.map((page) => (
              <div key={page.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{page.name}</div>
                  <div className="text-sm text-gray-500">{page.slug}</div>
                </div>
                <button
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/pages/${page.id}`, {...page, isVisible: !page.isVisible});
                      fetchAllData();
                      toast({ title: "Updated", description: `${page.name} visibility changed` });
                    } catch (e) {
                      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
                    }
                  }}
                  className={`p-2 rounded-lg transition-colors ${page.isVisible ? 'bg-green-100 text-green-600' : 'bg-gray-200 text-gray-400'}`}
                >
                  {page.isVisible ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                </button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // ==================== COMPANY INFO TAB ====================
  const CompanyInfoTab = () => {
    const [localInfo, setLocalInfo] = useState({...companyInfo});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
      setLocalInfo({...companyInfo});
    }, [companyInfo]);

    const handleSave = async () => {
      setSaving(true);
      try {
        const response = await axios.put(`${API}/company-info`, localInfo);
        if (response.data.companyInfo) {
          setCompanyInfo(response.data.companyInfo);
          toast({ title: "Saved!", description: "Company information updated and published" });
        } else {
          throw new Error('No data returned from API');
        }
      } catch (error) {
        console.error('Company Info save error:', error.response?.data || error);
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
        toast({ 
          title: "Save Failed", 
          description: `Error: ${errorMsg}`, 
          variant: "destructive" 
        });
      } finally {
        setSaving(false);
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Company Information</h2>
            <p className="text-gray-500">Global company details used across the website</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save & Publish'}
          </Button>
        </div>

        {/* Company Details */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" /> Company Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Registered Company Name</label>
                <Input 
                  value={localInfo.registeredName || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, registeredName: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trading Name</label>
                <Input 
                  value={localInfo.tradingName || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, tradingName: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Number</label>
                <Input 
                  value={localInfo.companyNumber || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, companyNumber: e.target.value})}
                  placeholder="e.g., 12345678"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
                <Input 
                  value={localInfo.vatNumber || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, vatNumber: e.target.value})}
                  placeholder="e.g., GB123456789"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year Founded</label>
                <Input 
                  value={localInfo.founded || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, founded: e.target.value})}
                  placeholder="e.g., 2021"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Tagline</label>
              <Textarea 
                value={localInfo.tagline || ''} 
                onChange={(e) => setLocalInfo({...localInfo, tagline: e.target.value})}
                placeholder="e.g., Building our future by engineering projects with generational value"
                rows={2}
              />
              <p className="text-xs text-gray-500 mt-1">Displayed in website footer and About page</p>
            </div>
          </CardContent>
        </Card>

        {/* Address */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" /> Registered Address
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                <Input 
                  value={localInfo.addressLine1 || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, addressLine1: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                <Input 
                  value={localInfo.addressLine2 || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, addressLine2: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                <Input 
                  value={localInfo.city || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, city: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
                <Input 
                  value={localInfo.county || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, county: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                <Input 
                  value={localInfo.postcode || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, postcode: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                <Input 
                  value={localInfo.country || ''} 
                  onChange={(e) => setLocalInfo({...localInfo, country: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Office Location Description</label>
              <Textarea 
                value={localInfo.officeLocationText || ''} 
                onChange={(e) => setLocalInfo({...localInfo, officeLocationText: e.target.value})}
                placeholder="e.g., Located in Central London, 5 minutes from Waterloo Station"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Google Maps Embed URL</label>
              <Input 
                value={localInfo.mapEmbedUrl || ''} 
                onChange={(e) => setLocalInfo({...localInfo, mapEmbedUrl: e.target.value})}
                placeholder="https://www.google.com/maps/embed?pb=..."
              />
              <p className="text-xs text-gray-500 mt-1">Paste the embed URL from Google Maps</p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Details */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Phone className="w-5 h-5" /> Contact Details
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">General Phone</label>
              <Input 
                value={localInfo.generalPhone || ''} 
                onChange={(e) => setLocalInfo({...localInfo, generalPhone: e.target.value})}
                placeholder="e.g., +44 20 1234 5678"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">General Email</label>
              <Input 
                value={localInfo.generalEmail || ''} 
                onChange={(e) => setLocalInfo({...localInfo, generalEmail: e.target.value})}
                placeholder="e.g., info@furd.co.uk"
              />
            </div>
          </CardContent>
        </Card>

        {/* Social Links */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" /> Social Media Links
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn URL</label>
              <Input 
                value={localInfo.linkedinUrl || ''} 
                onChange={(e) => setLocalInfo({...localInfo, linkedinUrl: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Twitter URL</label>
              <Input 
                value={localInfo.twitterUrl || ''} 
                onChange={(e) => setLocalInfo({...localInfo, twitterUrl: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Facebook URL</label>
              <Input 
                value={localInfo.facebookUrl || ''} 
                onChange={(e) => setLocalInfo({...localInfo, facebookUrl: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instagram URL</label>
              <Input 
                value={localInfo.instagramUrl || ''} 
                onChange={(e) => setLocalInfo({...localInfo, instagramUrl: e.target.value})}
              />
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} size="lg" className="w-full bg-green-600 hover:bg-green-700">
          <Save className="w-5 h-5 mr-2" /> {saving ? 'Saving...' : 'Save & Publish All Changes'}
        </Button>
      </div>
    );
  };

  // ==================== CONTACT SETTINGS TAB ====================
  const ContactSettingsTab = () => {
    const [localSettings, setLocalSettings] = useState({...contactSettings});
    const [saving, setSaving] = useState(false);
    const [testingEmail, setTestingEmail] = useState(false);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
      setLocalSettings({...contactSettings});
    }, [contactSettings]);

    const handleSave = async () => {
      setSaving(true);
      setTestResult(null);
      try {
        // Validate required fields
        if (!localSettings.defaultRecipient) {
          toast({ title: "Validation Error", description: "Default recipient email is required", variant: "destructive" });
          setSaving(false);
          return;
        }
        
        const response = await axios.put(`${API}/contact-settings`, localSettings);
        if (response.data.contactSettings) {
          setContactSettings(response.data.contactSettings);
          toast({ title: "Saved!", description: "Contact settings updated and published" });
        } else {
          throw new Error('No data returned from API');
        }
      } catch (error) {
        console.error('Contact Settings save error:', error.response?.data || error);
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
        toast({ 
          title: "Save Failed", 
          description: `Error: ${errorMsg}`, 
          variant: "destructive" 
        });
      } finally {
        setSaving(false);
      }
    };

    const handleTestEmail = async () => {
      setTestingEmail(true);
      setTestResult(null);
      
      const testEmail = localSettings.testModeEmail || localSettings.defaultRecipient;
      if (!testEmail) {
        toast({ title: "Email Required", description: "Please enter a recipient email address first", variant: "destructive" });
        setTestingEmail(false);
        return;
      }
      
      try {
        const response = await axios.post(`${API}/contact-settings/test-email`, {
          testEmail: testEmail,
          enquiryType: 'test'
        });
        
        if (response.data.success) {
          // Check if it's actually in simulation mode
          const isSimulated = response.data.note?.includes('simulation') || response.data.status === 'simulated';
          
          setTestResult({ 
            success: true, 
            message: response.data.message,
            simulated: isSimulated 
          });
          
          if (isSimulated) {
            toast({ 
              title: "⚠️ Simulation Mode", 
              description: `Test recorded for ${testEmail}. Note: Real email requires SMTP configuration.`
            });
          } else {
            toast({ 
              title: "✓ Test Email Sent", 
              description: `Email sent to ${testEmail}. Please check your inbox.` 
            });
          }
        } else {
          throw new Error(response.data.message || response.data.detail || 'Unknown error');
        }
      } catch (error) {
        console.error('[CMS] Test email error:', error.response?.data || error);
        const status = error.response?.status || '';
        const detail = error.response?.data?.detail || error.response?.data?.message || error.message;
        const errorMsg = status ? `Error ${status}: ${detail}` : detail;
        
        setTestResult({ success: false, message: errorMsg });
        toast({ 
          title: "Test Email Failed", 
          description: errorMsg, 
          variant: "destructive" 
        });
      } finally {
        setTestingEmail(false);
      }
    };

    const addRecipient = () => {
      const newRecipient = {
        enquiryType: 'general',
        primaryEmail: '',
        ccEmails: [],
        bccEmails: [],
        isActive: true
      };
      setLocalSettings({
        ...localSettings,
        recipients: [...(localSettings.recipients || []), newRecipient]
      });
    };

    const updateRecipient = (index, field, value) => {
      const updated = [...(localSettings.recipients || [])];
      updated[index] = {...updated[index], [field]: value};
      setLocalSettings({...localSettings, recipients: updated});
    };

    const removeRecipient = (index) => {
      const updated = [...(localSettings.recipients || [])];
      updated.splice(index, 1);
      setLocalSettings({...localSettings, recipients: updated});
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Contact Page Settings</h2>
            <p className="text-gray-500">Manage contact form, email routing, and page content</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleTestEmail} disabled={testingEmail} variant="outline">
              <TestTube className="w-4 h-4 mr-2" /> {testingEmail ? 'Sending...' : 'Send Test'}
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-green-600 hover:bg-green-700">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save & Publish'}
            </Button>
          </div>
        </div>

        {/* Page Content */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> Page Content
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page Title</label>
                <Input 
                  value={localSettings.pageTitle || ''} 
                  onChange={(e) => setLocalSettings({...localSettings, pageTitle: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page Subtitle</label>
                <Input 
                  value={localSettings.pageSubtitle || ''} 
                  onChange={(e) => setLocalSettings({...localSettings, pageSubtitle: e.target.value})}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Introduction Text</label>
              <Textarea 
                value={localSettings.introText || ''} 
                onChange={(e) => setLocalSettings({...localSettings, introText: e.target.value})}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Email Recipients */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Mail className="w-5 h-5" /> Email Recipients</span>
              <Button onClick={addRecipient} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-1" /> Add Recipient
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Recipient Email</label>
              <Input 
                value={localSettings.defaultRecipient || ''} 
                onChange={(e) => setLocalSettings({...localSettings, defaultRecipient: e.target.value})}
                placeholder="Fallback email if no specific routing"
              />
            </div>
            
            {(localSettings.recipients || []).map((recipient, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Recipient {index + 1}</h4>
                  <Button onClick={() => removeRecipient(index)} size="sm" variant="ghost" className="text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Enquiry Type</label>
                    <select
                      value={recipient.enquiryType}
                      onChange={(e) => updateRecipient(index, 'enquiryType', e.target.value)}
                      className="w-full border rounded p-2 text-sm"
                    >
                      {(localSettings.enquiryTypes || []).map(et => (
                        <option key={et.id} value={et.id}>{et.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Primary Email</label>
                    <Input 
                      value={recipient.primaryEmail || ''} 
                      onChange={(e) => updateRecipient(index, 'primaryEmail', e.target.value)}
                      placeholder="email@company.com"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">CC Emails (comma separated)</label>
                    <Input 
                      value={(recipient.ccEmails || []).join(', ')} 
                      onChange={(e) => updateRecipient(index, 'ccEmails', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">BCC Emails (comma separated)</label>
                    <Input 
                      value={(recipient.bccEmails || []).join(', ')} 
                      onChange={(e) => updateRecipient(index, 'bccEmails', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={recipient.isActive}
                    onChange={(e) => updateRecipient(index, 'isActive', e.target.checked)}
                    className="rounded"
                  />
                  Active
                </label>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Spam Protection */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" /> Spam Protection & Validation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                checked={localSettings.honeypotEnabled || false}
                onChange={(e) => setLocalSettings({...localSettings, honeypotEnabled: e.target.checked})}
                className="rounded"
              />
              <div>
                <div className="font-medium">Honeypot Protection</div>
                <div className="text-xs text-gray-500">Hidden field to catch automated spam bots</div>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                checked={localSettings.enableCaptcha || false}
                onChange={(e) => setLocalSettings({...localSettings, enableCaptcha: e.target.checked})}
                className="rounded"
              />
              <div>
                <div className="font-medium">Enable CAPTCHA</div>
                <div className="text-xs text-gray-500">Require human verification (requires setup)</div>
              </div>
            </label>
          </CardContent>
        </Card>

        {/* Auto Reply */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5" /> Auto-Reply Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={localSettings.enableAutoReply || false}
                onChange={(e) => setLocalSettings({...localSettings, enableAutoReply: e.target.checked})}
                className="rounded"
              />
              <span className="font-medium">Send auto-reply to form submitters</span>
            </label>
            {localSettings.enableAutoReply && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auto-Reply Subject</label>
                  <Input 
                    value={localSettings.autoReplySubject || ''} 
                    onChange={(e) => setLocalSettings({...localSettings, autoReplySubject: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Auto-Reply Message</label>
                  <Textarea 
                    value={localSettings.autoReplyMessage || ''} 
                    onChange={(e) => setLocalSettings({...localSettings, autoReplyMessage: e.target.value})}
                    rows={4}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Test Mode */}
        <Card className="border-0 shadow-lg border-yellow-200 bg-yellow-50">
          <CardHeader className="bg-yellow-100">
            <CardTitle className="flex items-center gap-2 text-yellow-800">
              <TestTube className="w-5 h-5" /> Test Mode
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={localSettings.testModeEnabled || false}
                onChange={(e) => setLocalSettings({...localSettings, testModeEnabled: e.target.checked})}
                className="rounded"
              />
              <span className="font-medium">Enable Test Mode</span>
            </label>
            <p className="text-sm text-yellow-700">When enabled, all form submissions will be sent to the test email below instead of actual recipients.</p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Test Email Address</label>
              <Input 
                value={localSettings.testModeEmail || ''} 
                onChange={(e) => setLocalSettings({...localSettings, testModeEmail: e.target.value})}
                placeholder="test@yourcompany.com"
              />
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleSave} disabled={saving} size="lg" className="w-full bg-green-600 hover:bg-green-700">
          <Save className="w-5 h-5 mr-2" /> {saving ? 'Saving...' : 'Save & Publish All Changes'}
        </Button>
      </div>
    );
  };

  // ==================== ACCREDITATION CARD COMPONENT ====================
  const AccreditationCard = ({ acc, onEdit, onDelete }) => {
    const [imgError, setImgError] = useState(false);
    
    return (
      <Card className={`border-0 shadow-lg overflow-hidden ${!acc.isActive ? 'opacity-60' : ''}`}>
        {acc.logo && (
          <div className="h-32 bg-gray-100 flex items-center justify-center p-4 relative">
            {imgError ? (
              <div className="flex flex-col items-center text-red-500">
                <AlertTriangle className="w-8 h-8 mb-1" />
                <span className="text-xs">Broken Image</span>
                <button 
                  onClick={onEdit}
                  className="text-xs text-blue-600 underline mt-1"
                >
                  Fix
                </button>
              </div>
            ) : (
              <img 
                src={acc.logo} 
                alt={acc.name} 
                className="max-h-full max-w-full object-contain"
                onError={() => setImgError(true)}
              />
            )}
          </div>
        )}
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-bold text-gray-900">{acc.name}</h3>
            <div className="flex gap-1">
              {imgError && <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Broken</span>}
              {!acc.isActive && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Inactive</span>}
              {(acc.displayOn || []).length === 0 && <span className="text-xs bg-yellow-100 text-yellow-600 px-2 py-0.5 rounded">Not displayed</span>}
            </div>
          </div>
          {acc.description && <p className="text-sm text-gray-600 mb-2 line-clamp-2">{acc.description}</p>}
          {acc.expiryDate && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
              <Calendar className="w-3 h-3" />
              Expires: {new Date(acc.expiryDate).toLocaleDateString()}
              {acc.reminderEnabled && <Clock className="w-3 h-3 ml-1 text-orange-500" />}
            </div>
          )}
          {(acc.displayOn || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {acc.displayOn.map(page => (
                <span key={page} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                  {page}
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={onEdit} className="flex-1 bg-blue-600 hover:bg-blue-700">
              <Edit className="w-4 h-4 mr-1" /> Edit
            </Button>
            {acc.link && (
              <Button size="sm" variant="outline" asChild>
                <a href={acc.link} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={onDelete} className="text-red-500 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // ==================== ACCREDITATIONS TAB ====================
  const AccreditationsTab = () => {
    const [editingAcc, setEditingAcc] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [newAcc, setNewAcc] = useState({
      name: '', logo: '', description: '', link: '',
      expiryDate: '', reminderEnabled: false,
      displayOn: [], order: 0, isActive: true
    });

    const handleSave = async (acc) => {
      try {
        // Validate required fields
        if (!acc.name?.trim()) {
          toast({ title: "Validation Error", description: "Name is required", variant: "destructive" });
          return;
        }
        
        // Ensure displayOn is an array
        const saveData = {
          ...acc,
          displayOn: acc.displayOn || [],
          order: acc.order || 0
        };
        
        let response;
        if (acc.id) {
          response = await axios.put(`${API}/accreditations/${acc.id}`, saveData);
          if (response.data.accreditation) {
            toast({ title: "Updated!", description: "Accreditation saved and published" });
          }
        } else {
          response = await axios.post(`${API}/accreditations`, saveData);
          if (response.data.accreditation) {
            toast({ title: "Created!", description: "Accreditation added and published" });
          }
        }
        
        fetchAllData();
        setEditingAcc(null);
        setIsAdding(false);
        setNewAcc({ name: '', logo: '', description: '', link: '', expiryDate: '', reminderEnabled: false, displayOn: [], order: 0, isActive: true });
      } catch (error) {
        console.error('Accreditation save error:', error.response?.data || error);
        const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Unknown error';
        toast({ 
          title: "Save Failed", 
          description: `Error: ${errorMsg}`, 
          variant: "destructive" 
        });
      }
    };

    const handleDelete = async (id) => {
      if (!window.confirm('Move this accreditation to trash?')) return;
      try {
        await axios.delete(`${API}/accreditations/${id}`);
        toast({ title: "Deleted", description: "Moved to trash" });
        fetchAllData();
      } catch (error) {
        toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
      }
    };

    const displayOptions = [
      { id: 'homepage', label: 'Homepage' },
      { id: 'about', label: 'About Page' },
      { id: 'footer', label: 'Footer' },
    ];

    const AccreditationForm = ({ acc, onSave, onCancel }) => {
      const [form, setForm] = useState({...acc});
      
      const toggleDisplayOn = (page) => {
        const current = form.displayOn || [];
        if (current.includes(page)) {
          setForm({...form, displayOn: current.filter(p => p !== page)});
        } else {
          setForm({...form, displayOn: [...current, page]});
        }
      };

      return (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <Input 
                  value={form.name || ''} 
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  placeholder="e.g., ISO 9001:2015"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Website Link</label>
                <Input 
                  value={form.link || ''} 
                  onChange={(e) => setForm({...form, link: e.target.value})}
                  placeholder="https://..."
                />
              </div>
            </div>
            
            <ImageUploader 
              label="Logo"
              value={form.logo}
              onChange={(val) => setForm({...form, logo: val})}
              folder="Logos"
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea 
                value={form.description || ''} 
                onChange={(e) => setForm({...form, description: e.target.value})}
                rows={2}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry/Renewal Date</label>
                <Input 
                  type="date"
                  value={form.expiryDate || ''} 
                  onChange={(e) => setForm({...form, expiryDate: e.target.value})}
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={form.reminderEnabled || false}
                    onChange={(e) => setForm({...form, reminderEnabled: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm">Enable renewal reminder</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display On (where this accreditation appears)</label>
              <div className="flex flex-wrap gap-2">
                {displayOptions.map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`display-on-${opt.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleDisplayOn(opt.id);
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                      (form.displayOn || []).includes(opt.id)
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Select which pages should display this accreditation</p>
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isActive !== false}
                onChange={(e) => setForm({...form, isActive: e.target.checked})}
                className="rounded"
              />
              <span className="text-sm font-medium">Active (visible on website)</span>
            </label>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => onSave(form)} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" /> Save
              </Button>
              <Button onClick={onCancel} variant="outline">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      );
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Accreditations & Memberships</h2>
            <p className="text-gray-500">Manage certifications, accreditations, and professional memberships</p>
          </div>
          {!isAdding && (
            <Button onClick={() => setIsAdding(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" /> Add Accreditation
            </Button>
          )}
        </div>

        {isAdding && (
          <AccreditationForm 
            acc={newAcc} 
            onSave={handleSave} 
            onCancel={() => setIsAdding(false)} 
          />
        )}

        {editingAcc && (
          <AccreditationForm 
            acc={editingAcc} 
            onSave={handleSave} 
            onCancel={() => setEditingAcc(null)} 
          />
        )}

        {!isAdding && !editingAcc && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {accreditations.length === 0 ? (
              <div className="col-span-full text-center py-12 bg-gray-50 rounded-lg">
                <Award className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No accreditations yet</p>
                <p className="text-sm text-gray-400">Add certifications and memberships to display on your website</p>
              </div>
            ) : (
              accreditations.map((acc) => (
                <AccreditationCard 
                  key={acc.id} 
                  acc={acc} 
                  onEdit={() => setEditingAcc(acc)} 
                  onDelete={() => handleDelete(acc.id)} 
                />
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // ==================== NEWS MANAGEMENT TAB ====================
  const NewsManagementTab = () => {
    const [activeNewsTab, setActiveNewsTab] = useState('articles');
    const [sources, setSources] = useState([]);
    const [articles, setArticles] = useState([]);
    const [newsSettings, setNewsSettings] = useState({});
    const [loadingNews, setLoadingNews] = useState(true);
    const [fetching, setFetching] = useState(false);
    const [editingSource, setEditingSource] = useState(null);
    const [newSource, setNewSource] = useState({ name: '', url: '', category: 'general', enabled: true });
    const [showAddSource, setShowAddSource] = useState(false);
    const [articlesPage, setArticlesPage] = useState(1);
    const [totalArticles, setTotalArticles] = useState(0);
    
    // Fetch news data
    useEffect(() => {
      fetchNewsSources();
      fetchNewsArticles();
      fetchNewsSettings();
    }, []);
    
    const fetchNewsSources = async () => {
      try {
        const res = await axios.get(`${API}/news/sources`);
        if (res.data.success) {
          setSources(res.data.sources || []);
        }
      } catch (err) {
        console.error('Error fetching sources:', err);
      }
    };
    
    const fetchNewsArticles = async (page = 1) => {
      try {
        setLoadingNews(true);
        const res = await axios.get(`${API}/news/admin?limit=10&page=${page}`);
        if (res.data.success) {
          setArticles(res.data.articles || []);
          setTotalArticles(res.data.total || 0);
          setArticlesPage(page);
        }
      } catch (err) {
        console.error('Error fetching articles:', err);
      } finally {
        setLoadingNews(false);
      }
    };
    
    const fetchNewsSettings = async () => {
      try {
        const res = await axios.get(`${API}/news/settings`);
        if (res.data.success) {
          setNewsSettings(res.data.settings || {});
        }
      } catch (err) {
        console.error('Error fetching news settings:', err);
      }
    };
    
    const handleFetchNews = async () => {
      setFetching(true);
      try {
        const res = await axios.post(`${API}/news/fetch`);
        if (res.data.success) {
          toast({
            title: "News Fetched",
            description: `Fetched ${res.data.fetched} new articles from ${res.data.sources_processed} sources.`
          });
          fetchNewsArticles();
          fetchNewsSources();
        }
        if (res.data.errors?.length > 0) {
          toast({
            title: "Some Sources Failed",
            description: res.data.errors.map(e => e.source).join(', '),
            variant: "destructive"
          });
        }
      } catch (err) {
        toast({ title: "Error", description: "Failed to fetch news", variant: "destructive" });
      } finally {
        setFetching(false);
      }
    };
    
    const handleAddSource = async () => {
      if (!newSource.name || !newSource.url) {
        toast({ title: "Error", description: "Name and URL are required", variant: "destructive" });
        return;
      }
      try {
        const res = await axios.post(`${API}/news/sources`, newSource);
        if (res.data.success) {
          toast({ title: "Source Added", description: `${newSource.name} added successfully` });
          setSources([...sources, res.data.source]);
          setNewSource({ name: '', url: '', category: 'general', enabled: true });
          setShowAddSource(false);
        }
      } catch (err) {
        toast({ title: "Error", description: err.response?.data?.detail || "Failed to add source", variant: "destructive" });
      }
    };
    
    const handleDeleteSource = async (sourceId) => {
      if (!window.confirm('Delete this source and all its articles?')) return;
      try {
        await axios.delete(`${API}/news/sources/${sourceId}`);
        toast({ title: "Deleted", description: "Source removed" });
        setSources(sources.filter(s => s.id !== sourceId));
        fetchNewsArticles();
      } catch (err) {
        toast({ title: "Error", description: "Failed to delete source", variant: "destructive" });
      }
    };
    
    const handleToggleSource = async (source) => {
      try {
        await axios.patch(`${API}/news/sources/${source.id}`, { enabled: !source.enabled });
        setSources(sources.map(s => s.id === source.id ? {...s, enabled: !s.enabled} : s));
      } catch (err) {
        toast({ title: "Error", description: "Failed to update source", variant: "destructive" });
      }
    };
    
    const handleToggleArticle = async (article, field) => {
      try {
        const newValue = !article[field];
        await axios.patch(`${API}/news/articles/${article.id}`, { [field]: newValue });
        setArticles(articles.map(a => a.id === article.id ? {...a, [field]: newValue} : a));
      } catch (err) {
        toast({ title: "Error", description: "Failed to update article", variant: "destructive" });
      }
    };
    
    const handleDeleteArticle = async (articleId) => {
      if (!window.confirm('Delete and blocklist this article?')) return;
      try {
        await axios.delete(`${API}/news/articles/${articleId}`);
        toast({ title: "Deleted", description: "Article removed and blocklisted" });
        setArticles(articles.filter(a => a.id !== articleId));
      } catch (err) {
        toast({ title: "Error", description: "Failed to delete article", variant: "destructive" });
      }
    };
    
    const handleSaveSettings = async () => {
      try {
        await axios.put(`${API}/news/settings`, newsSettings);
        toast({ title: "Saved", description: "News settings updated" });
      } catch (err) {
        toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
      }
    };
    
    const formatDate = (dateStr) => {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Newspaper className="w-6 h-6" /> Industry News
            </h2>
            <p className="text-gray-500 mt-1">Manage RSS sources and curate news articles</p>
          </div>
          <Button onClick={handleFetchNews} disabled={fetching}>
            <RefreshCw className={`w-4 h-4 mr-2 ${fetching ? 'animate-spin' : ''}`} />
            {fetching ? 'Fetching...' : 'Fetch News Now'}
          </Button>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 border-b">
          {['articles', 'sources', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveNewsTab(tab)}
              className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
                activeNewsTab === tab 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        
        {/* Articles Tab */}
        {activeNewsTab === 'articles' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>News Articles ({totalArticles})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingNews ? (
                <div className="text-center py-8 text-gray-500">Loading articles...</div>
              ) : articles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Newspaper className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No articles yet. Click "Fetch News Now" to pull from RSS sources.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {articles.map((article) => (
                    <div 
                      key={article.id} 
                      className={`flex items-start gap-4 p-4 border rounded-lg ${article.hidden ? 'bg-gray-50 opacity-60' : ''}`}
                    >
                      {article.thumbnail && (
                        <img src={article.thumbnail} alt="" className="w-20 h-14 object-cover rounded" />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 line-clamp-1">{article.title}</h4>
                        <p className="text-sm text-gray-500 line-clamp-1">{article.excerpt}</p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                          <span>{article.sourceName}</span>
                          <span>•</span>
                          <span>{formatDate(article.publishDate)}</span>
                          <span>•</span>
                          <span className="capitalize">{article.category}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={article.pinned ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleToggleArticle(article, 'pinned')}
                          title={article.pinned ? 'Unpin' : 'Pin to top'}
                        >
                          <Pin className="w-4 h-4" />
                        </Button>
                        <Button
                          variant={article.hidden ? "outline" : "ghost"}
                          size="sm"
                          onClick={() => handleToggleArticle(article, 'hidden')}
                          title={article.hidden ? 'Show' : 'Hide'}
                        >
                          {article.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteArticle(article.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <a
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-400 hover:text-blue-600"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  ))}
                  
                  {/* Pagination */}
                  {totalArticles > 10 && (
                    <div className="flex justify-center gap-2 pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchNewsArticles(articlesPage - 1)}
                        disabled={articlesPage === 1}
                      >
                        Previous
                      </Button>
                      <span className="px-4 py-2 text-sm text-gray-500">
                        Page {articlesPage} of {Math.ceil(totalArticles / 10)}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchNewsArticles(articlesPage + 1)}
                        disabled={articlesPage >= Math.ceil(totalArticles / 10)}
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Sources Tab */}
        {activeNewsTab === 'sources' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Rss className="w-5 h-5" /> RSS Sources
                </span>
                <Button size="sm" onClick={() => setShowAddSource(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Add Source
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Add Source Form */}
              {showAddSource && (
                <div className="p-4 mb-4 border rounded-lg bg-blue-50">
                  <h4 className="font-medium mb-3">Add New RSS Source</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      placeholder="Source Name"
                      value={newSource.name}
                      onChange={(e) => setNewSource({...newSource, name: e.target.value})}
                    />
                    <Input
                      placeholder="RSS Feed URL"
                      value={newSource.url}
                      onChange={(e) => setNewSource({...newSource, url: e.target.value})}
                    />
                    <select
                      className="border rounded-md px-3 py-2"
                      value={newSource.category}
                      onChange={(e) => setNewSource({...newSource, category: e.target.value})}
                    >
                      <option value="general">General</option>
                      <option value="mep">MEP</option>
                      <option value="construction">Construction</option>
                      <option value="sustainability">Sustainability</option>
                      <option value="regulations">Regulations</option>
                      <option value="bim">BIM</option>
                    </select>
                    <div className="flex gap-2">
                      <Button onClick={handleAddSource}>Add Source</Button>
                      <Button variant="outline" onClick={() => setShowAddSource(false)}>Cancel</Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Sources List */}
              {sources.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Rss className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No RSS sources configured. Add a source to start fetching news.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sources.map((source) => (
                    <div key={source.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${source.enabled ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <div>
                          <h4 className="font-medium">{source.name}</h4>
                          <p className="text-sm text-gray-500 truncate max-w-md">{source.url}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                            <span className="capitalize bg-gray-100 px-2 py-0.5 rounded">{source.category}</span>
                            <span>•</span>
                            <span>{source.articleCount || 0} articles</span>
                            {source.lastFetch && (
                              <>
                                <span>•</span>
                                <span>Last fetch: {formatDate(source.lastFetch)}</span>
                              </>
                            )}
                            {source.lastError && (
                              <span className="text-red-500" title={source.lastError}>⚠️ Error</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant={source.enabled ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleToggleSource(source)}
                        >
                          {source.enabled ? 'Enabled' : 'Disabled'}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteSource(source.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Settings Tab */}
        {activeNewsTab === 'settings' && (
          <Card>
            <CardHeader>
              <CardTitle>Display Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">Show on Homepage</h4>
                  <p className="text-sm text-gray-500">Display news section on the homepage</p>
                </div>
                <Button
                  variant={newsSettings.showOnHomepage ? "default" : "outline"}
                  onClick={() => setNewsSettings({...newsSettings, showOnHomepage: !newsSettings.showOnHomepage})}
                >
                  {newsSettings.showOnHomepage ? 'Shown' : 'Hidden'}
                </Button>
              </div>
              
              <div>
                <label className="block font-medium mb-2">Items on Homepage</label>
                <Input
                  type="number"
                  min="1"
                  max="12"
                  value={newsSettings.homepageCount || 3}
                  onChange={(e) => setNewsSettings({...newsSettings, homepageCount: parseInt(e.target.value)})}
                  className="w-32"
                />
              </div>
              
              <div>
                <label className="block font-medium mb-2">Layout Style</label>
                <select
                  className="border rounded-md px-3 py-2"
                  value={newsSettings.layout || 'grid'}
                  onChange={(e) => setNewsSettings({...newsSettings, layout: e.target.value})}
                >
                  <option value="grid">Grid</option>
                  <option value="list">List</option>
                </select>
              </div>
              
              <hr className="my-6" />
              
              {/* Auto-Fetch Scheduling */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Clock className="w-5 h-5" /> Auto-Fetch Schedule
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Automatically fetch news from RSS sources on a schedule</p>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium">Enable Auto-Fetch</h4>
                    <p className="text-sm text-gray-500">Automatically fetch news at scheduled times</p>
                  </div>
                  <Button
                    variant={newsSettings.autoFetchEnabled ? "default" : "outline"}
                    onClick={() => setNewsSettings({...newsSettings, autoFetchEnabled: !newsSettings.autoFetchEnabled})}
                    className={newsSettings.autoFetchEnabled ? "bg-green-600 hover:bg-green-700" : ""}
                  >
                    {newsSettings.autoFetchEnabled ? 'Enabled' : 'Disabled'}
                  </Button>
                </div>
                
                {newsSettings.autoFetchEnabled && (
                  <div className="space-y-4 p-4 border rounded-lg bg-blue-50/50">
                    {/* Frequency */}
                    <div>
                      <label className="block font-medium mb-2">Frequency</label>
                      <select
                        className="border rounded-md px-3 py-2 w-full max-w-xs"
                        value={newsSettings.fetchFrequency || 'weekly'}
                        onChange={(e) => setNewsSettings({...newsSettings, fetchFrequency: e.target.value})}
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    
                    {/* Day Selection (for weekly) */}
                    {newsSettings.fetchFrequency === 'weekly' && (
                      <div>
                        <label className="block font-medium mb-2">Day of Week</label>
                        <select
                          className="border rounded-md px-3 py-2 w-full max-w-xs"
                          value={newsSettings.fetchDay || 'monday'}
                          onChange={(e) => setNewsSettings({...newsSettings, fetchDay: e.target.value})}
                        >
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                    )}
                    
                    {/* Day of Month (for monthly) */}
                    {newsSettings.fetchFrequency === 'monthly' && (
                      <div>
                        <label className="block font-medium mb-2">Day of Month</label>
                        <Input
                          type="number"
                          min="1"
                          max="28"
                          value={newsSettings.fetchDayOfMonth || 1}
                          onChange={(e) => setNewsSettings({...newsSettings, fetchDayOfMonth: parseInt(e.target.value)})}
                          className="w-32"
                        />
                        <p className="text-xs text-gray-500 mt-1">Choose between 1-28 to avoid month-end issues</p>
                      </div>
                    )}
                    
                    {/* Time */}
                    <div>
                      <label className="block font-medium mb-2">Time (24-hour format)</label>
                      <Input
                        type="time"
                        value={newsSettings.fetchTime || '08:00'}
                        onChange={(e) => setNewsSettings({...newsSettings, fetchTime: e.target.value})}
                        className="w-40"
                      />
                    </div>
                    
                    {/* Status Display */}
                    <div className="mt-4 p-3 bg-white rounded-lg border">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Last Auto-Fetch:</span>
                          <p className="font-medium">{newsSettings.lastAutoFetch ? new Date(newsSettings.lastAutoFetch).toLocaleString() : 'Never'}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Next Scheduled:</span>
                          <p className="font-medium text-blue-600">{newsSettings.nextScheduledFetch ? new Date(newsSettings.nextScheduledFetch).toLocaleString() : 'Not scheduled'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <Button onClick={handleSaveSettings}>
                <Save className="w-4 h-4 mr-2" /> Save Settings
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    );
  };

  // ==================== CAREER PROGRESSION TAB ====================
  const CareerProgressionTab = () => {
    const [editingLevel, setEditingLevel] = useState(null);
    const [isAdding, setIsAdding] = useState(false);
    const [editingSettings, setEditingSettings] = useState(false);
    const [localSettings, setLocalSettings] = useState({...careerProgressionSettings});
    const [newLevel, setNewLevel] = useState({
      title: '', description: '', responsibilities: [], skillsAndTraining: [],
      durationRange: '', icon: '', image: '', order: 0, isVisible: true
    });
    const [savingSettings, setSavingSettings] = useState(false);

    useEffect(() => {
      setLocalSettings({...careerProgressionSettings});
    }, [careerProgressionSettings]);

    const handleSaveLevel = async (level) => {
      try {
        if (level.id) {
          await axios.put(`${API}/career-levels/${level.id}`, level);
          toast({ title: "Updated!", description: "Career level saved" });
        } else {
          await axios.post(`${API}/career-levels`, level);
          toast({ title: "Created!", description: "Career level added" });
        }
        fetchAllData();
        setEditingLevel(null);
        setIsAdding(false);
        setNewLevel({ title: '', description: '', responsibilities: [], skillsAndTraining: [], durationRange: '', icon: '', image: '', order: 0, isVisible: true });
      } catch (error) {
        toast({ title: "Error", description: "Failed to save", variant: "destructive" });
      }
    };

    const handleDeleteLevel = async (id) => {
      if (!window.confirm('Delete this career level?')) return;
      try {
        await axios.delete(`${API}/career-levels/${id}`);
        toast({ title: "Deleted", description: "Career level removed" });
        fetchAllData();
      } catch (error) {
        toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
      }
    };

    const handleSaveSettings = async () => {
      setSavingSettings(true);
      try {
        await axios.put(`${API}/career-progression-settings`, localSettings);
        toast({ title: "Saved!", description: "Settings updated" });
        fetchAllData();
        setEditingSettings(false);
      } catch (error) {
        toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
      } finally {
        setSavingSettings(false);
      }
    };

    const moveLevel = async (index, direction) => {
      const newLevels = [...careerLevels];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= newLevels.length) return;
      
      [newLevels[index], newLevels[targetIndex]] = [newLevels[targetIndex], newLevels[index]];
      const orders = newLevels.map((level, i) => ({ id: level.id, order: i }));
      
      try {
        await axios.put(`${API}/career-levels/reorder`, orders);
        fetchAllData();
      } catch (error) {
        toast({ title: "Error", description: "Failed to reorder", variant: "destructive" });
      }
    };

    const CareerLevelForm = ({ level, onSave, onCancel }) => {
      const [form, setForm] = useState({...level});

      return (
        <Card className="border-0 shadow-lg">
          <CardHeader className="bg-blue-50">
            <CardTitle>{level.id ? 'Edit Career Level' : 'Add New Career Level'}</CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Level Title *</label>
                <Input 
                  value={form.title || ''} 
                  onChange={(e) => setForm({...form, title: e.target.value})}
                  placeholder="e.g., Graduate Engineer, Senior Engineer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Duration Range</label>
                <Input 
                  value={form.durationRange || ''} 
                  onChange={(e) => setForm({...form, durationRange: e.target.value})}
                  placeholder="e.g., 0-2 years, 5+ years"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Textarea 
                value={form.description || ''} 
                onChange={(e) => setForm({...form, description: e.target.value})}
                rows={3}
                placeholder="Brief description of this career level..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Key Responsibilities (one per line)</label>
              <Textarea 
                value={(form.responsibilities || []).join('\n')} 
                onChange={(e) => setForm({
                  ...form, 
                  responsibilities: e.target.value.split('\n').filter(line => line.trim())
                })}
                rows={4}
                placeholder="e.g.&#10;Design MEP systems&#10;Prepare technical drawings&#10;Coordinate with contractors"
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">{(form.responsibilities || []).length} responsibilities</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Skills & Training (one per line)</label>
              <Textarea 
                value={(form.skillsAndTraining || []).join('\n')} 
                onChange={(e) => setForm({
                  ...form, 
                  skillsAndTraining: e.target.value.split('\n').filter(line => line.trim())
                })}
                rows={4}
                placeholder="e.g.&#10;AutoCAD proficiency&#10;Building regulations knowledge&#10;Chartership support"
                className="font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">{(form.skillsAndTraining || []).length} skills</p>
            </div>

            <ImageUploader 
              label="Level Image (optional)"
              value={form.image}
              onChange={(val) => setForm({...form, image: val})}
              folder="Other"
            />

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.isVisible !== false}
                  onChange={(e) => setForm({...form, isVisible: e.target.checked})}
                  className="rounded"
                />
                <span className="text-sm font-medium">Visible on website</span>
              </label>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Display Order:</label>
                <Input 
                  type="number"
                  value={form.order || 0} 
                  onChange={(e) => setForm({...form, order: parseInt(e.target.value) || 0})}
                  className="w-20"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button onClick={() => onSave(form)} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" /> Save Level
              </Button>
              <Button onClick={onCancel} variant="outline">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      );
    };

    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Career Progression</h2>
            <p className="text-gray-500">Manage career pathway levels displayed on the Careers page</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setEditingSettings(!editingSettings)} variant="outline">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
            {!isAdding && (
              <Button onClick={() => setIsAdding(true)} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="w-4 h-4 mr-2" /> Add Level
              </Button>
            )}
          </div>
        </div>

        {/* Section Settings */}
        {editingSettings && (
          <Card className="border-0 shadow-lg border-purple-200 bg-purple-50">
            <CardHeader className="bg-purple-100">
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" /> Career Progression Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                  <Input 
                    value={localSettings.sectionTitle || ''} 
                    onChange={(e) => setLocalSettings({...localSettings, sectionTitle: e.target.value})}
                    placeholder="e.g., Career Progression"
                  />
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={localSettings.showOnCareersPage !== false}
                      onChange={(e) => setLocalSettings({...localSettings, showOnCareersPage: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Show on Careers Page</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={localSettings.showOnAboutPage || false}
                      onChange={(e) => setLocalSettings({...localSettings, showOnAboutPage: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Show on About Page</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Introduction Text</label>
                <Textarea 
                  value={localSettings.introText || ''} 
                  onChange={(e) => setLocalSettings({...localSettings, introText: e.target.value})}
                  rows={2}
                  placeholder="Describe your career pathway..."
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mentoring Support</label>
                  <Textarea 
                    value={localSettings.mentoringText || ''} 
                    onChange={(e) => setLocalSettings({...localSettings, mentoringText: e.target.value})}
                    rows={2}
                    placeholder="How you support mentoring..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CPD Support</label>
                  <Textarea 
                    value={localSettings.cpdSupportText || ''} 
                    onChange={(e) => setLocalSettings({...localSettings, cpdSupportText: e.target.value})}
                    rows={2}
                    placeholder="CPD opportunities offered..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Chartership Support</label>
                  <Textarea 
                    value={localSettings.chartershipSupportText || ''} 
                    onChange={(e) => setLocalSettings({...localSettings, chartershipSupportText: e.target.value})}
                    rows={2}
                    placeholder="How you support Chartership..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Benefits (one per line)</label>
                <Textarea 
                  value={(localSettings.benefits || []).join('\n')} 
                  onChange={(e) => setLocalSettings({
                    ...localSettings, 
                    benefits: e.target.value.split('\n').filter(line => line.trim())
                  })}
                  rows={3}
                  placeholder="e.g.&#10;Flexible working&#10;25 days holiday&#10;Professional development budget"
                  className="font-mono text-sm"
                />
              </div>

              <div className="flex gap-2">
                <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" /> {savingSettings ? 'Saving...' : 'Save Settings'}
                </Button>
                <Button onClick={() => setEditingSettings(false)} variant="outline">Close</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add New Level Form */}
        {isAdding && (
          <CareerLevelForm 
            level={newLevel} 
            onSave={handleSaveLevel} 
            onCancel={() => setIsAdding(false)} 
          />
        )}

        {/* Edit Level Form */}
        {editingLevel && (
          <CareerLevelForm 
            level={editingLevel} 
            onSave={handleSaveLevel} 
            onCancel={() => setEditingLevel(null)} 
          />
        )}

        {/* Career Levels List */}
        {!isAdding && !editingLevel && (
          <div className="space-y-4">
            {careerLevels.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <TrendingUp className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No career levels defined yet</p>
                <p className="text-sm text-gray-400">Add levels like Graduate, Engineer, Senior Engineer, etc.</p>
              </div>
            ) : (
              careerLevels.map((level, index) => (
                <Card key={level.id} className={`border-0 shadow-lg overflow-hidden ${!level.isVisible ? 'opacity-60' : ''}`}>
                  <div className="flex items-stretch">
                    {/* Reorder Controls */}
                    <div className="bg-gray-100 flex flex-col justify-center px-2 border-r">
                      <button 
                        onClick={() => moveLevel(index, 'up')}
                        disabled={index === 0}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronUp className="w-5 h-5" />
                      </button>
                      <div className="text-center text-sm font-bold text-gray-500 py-1">{index + 1}</div>
                      <button 
                        onClick={() => moveLevel(index, 'down')}
                        disabled={index === careerLevels.length - 1}
                        className="p-1 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ChevronDown className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Level Content */}
                    <div className="flex-1 p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-bold text-lg text-gray-900">{level.title}</h3>
                          {level.durationRange && (
                            <p className="text-sm text-blue-600">{level.durationRange}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {!level.isVisible && (
                            <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Hidden</span>
                          )}
                        </div>
                      </div>
                      
                      {level.description && (
                        <p className="text-gray-600 text-sm mb-3">{level.description}</p>
                      )}

                      <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-3">
                        {level.responsibilities?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Check className="w-4 h-4 text-green-500" />
                            {level.responsibilities.length} responsibilities
                          </span>
                        )}
                        {level.skillsAndTraining?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <Award className="w-4 h-4 text-blue-500" />
                            {level.skillsAndTraining.length} skills
                          </span>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => setEditingLevel(level)} className="bg-blue-600 hover:bg-blue-700">
                          <Edit className="w-4 h-4 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteLevel(level.id)} className="text-red-500 border-red-200 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  // ==================== VERSION HISTORY COMPONENT ====================
  const VersionHistoryPanel = ({ contentType, contentId, onRestore }) => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const fetchVersions = async () => {
        try {
          const response = await axios.get(`${API}/versions/${contentType}/${contentId}`);
          setVersions(response.data.versions || []);
        } catch (error) {
          console.error('Error fetching versions:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchVersions();
    }, [contentType, contentId]);

    const handleRestore = async (versionId) => {
      if (!window.confirm('Restore this version? Current changes will be saved as a new version first.')) return;
      try {
        await axios.post(`${API}/versions/restore/${versionId}`);
        toast({ title: "Restored!", description: "Version restored successfully" });
        if (onRestore) onRestore();
      } catch (error) {
        toast({ title: "Error", description: "Failed to restore", variant: "destructive" });
      }
    };

    if (loading) return <div className="text-center py-4"><RefreshCw className="w-6 h-6 animate-spin mx-auto" /></div>;

    return (
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gray-50">
          <CardTitle className="flex items-center gap-2 text-sm">
            <History className="w-4 h-4" /> Version History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 max-h-64 overflow-y-auto">
          {versions.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No previous versions</p>
          ) : (
            <div className="space-y-2">
              {versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-sm">
                  <div>
                    <div className="text-gray-700">{new Date(v.createdAt).toLocaleString()}</div>
                    <div className="text-xs text-gray-500">{v.changeDescription || 'Auto-saved'}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => handleRestore(v.id)}>
                    <Undo2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // ==================== MAIN RENDER ====================
  return (
    <div className="min-h-screen bg-gray-100">
      <SEO title="CMS - Content Management" />
      
      {/* Header */}
      <div className="bg-slate-900 text-white py-4 px-6 shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Settings className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">FURD CMS</h1>
              <p className="text-xs text-gray-400">Content Management System</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* User info */}
            <div className="flex items-center gap-2 text-sm">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </div>
              <div className="hidden md:block">
                <p className="text-gray-200 font-medium text-sm">{user?.name}</p>
                <p className="text-gray-400 text-xs capitalize">{user?.role}</p>
              </div>
            </div>
            <a href="/" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-300 hover:text-white flex items-center gap-1">
              <Eye className="w-4 h-4" /> View Site
            </a>
            <Button onClick={logout} variant="outline" size="sm" className="border-gray-600 text-gray-300 hover:bg-gray-800" data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-1" /> Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content with Sidebar Layout */}
      <div className="flex min-h-screen">
        {/* Sidebar Navigation */}
        <aside className="w-64 bg-gray-900 text-white flex-shrink-0 sticky top-0 h-screen overflow-y-auto">
          <div className="p-4 border-b border-gray-800">
            <h2 className="text-lg font-bold text-white">CMS</h2>
            <p className="text-xs text-gray-400">Content Management</p>
          </div>
          
          <nav className="p-3 space-y-1">
            {/* Dashboard */}
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'dashboard' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </button>
            
            {/* Analytics */}
            <button
              onClick={() => setActiveTab('analytics')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === 'analytics' 
                  ? 'bg-blue-600 text-white' 
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
              data-testid="analytics-tab-btn"
            >
              <Activity className="w-4 h-4" /> Analytics
            </button>

            {/* CONTENT Section */}
            <div className="pt-4">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Content</p>
              <button
                onClick={() => setActiveTab('homepage')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'homepage' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Home className="w-4 h-4" /> Homepage
              </button>
              <button
                onClick={() => setActiveTab('about-page')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'about-page' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Building2 className="w-4 h-4" /> About Page
              </button>
              <button
                onClick={() => setActiveTab('pages')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'pages' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Globe className="w-4 h-4" /> Pages
              </button>
              <button
                onClick={() => setActiveTab('services')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'services' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Wrench className="w-4 h-4" /> Services
              </button>
              <button
                onClick={() => setActiveTab('services-overview')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'services-overview' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <LayoutDashboard className="w-4 h-4" /> Services Overview
              </button>
              <button
                onClick={() => setActiveTab('sectors')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'sectors' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Layers className="w-4 h-4" /> Sectors
              </button>
              <button
                onClick={() => setActiveTab('projects')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'projects' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <FolderOpen className="w-4 h-4" /> Projects
              </button>
            </div>

            {/* PEOPLE Section */}
            <div className="pt-4">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">People</p>
              <button
                onClick={() => setActiveTab('team')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'team' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Users className="w-4 h-4" /> Team
              </button>
              <button
                onClick={() => setActiveTab('jobs')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'jobs' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Briefcase className="w-4 h-4" /> Careers
              </button>
              <button
                onClick={() => setActiveTab('career-progression')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'career-progression' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <TrendingUp className="w-4 h-4" /> Career Path
              </button>
            </div>

            {/* ASSETS Section */}
            <div className="pt-4">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Assets</p>
              <button
                onClick={() => setActiveTab('images')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'images' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <ImageIcon className="w-4 h-4" /> Image Library
              </button>
              <button
                onClick={() => setActiveTab('news')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'news' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Newspaper className="w-4 h-4" /> Industry News
              </button>
            </div>

            {/* SETTINGS Section */}
            <div className="pt-4">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Settings</p>
              <button
                onClick={() => setActiveTab('company')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'company' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Building2 className="w-4 h-4" /> Company Info
              </button>
              <button
                onClick={() => setActiveTab('contact')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'contact' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Mail className="w-4 h-4" /> Contact Settings
              </button>
              <button
                onClick={() => setActiveTab('accreditations')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'accreditations' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Award className="w-4 h-4" /> Accreditations
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'settings' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <Settings className="w-4 h-4" /> Site Settings
              </button>
            </div>

            {/* SYSTEM Section */}
            <div className="pt-4 border-t border-gray-800 mt-4">
              <p className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">System</p>
              {/* User Management - Admin only */}
              {isAdmin() && (
                <button
                  onClick={() => setActiveTab('users')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeTab === 'users' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                  }`}
                  data-testid="users-tab-btn"
                >
                  <Users className="w-4 h-4" /> User Management
                </button>
              )}
              {/* Security (My Account) - available to all users */}
              <button
                onClick={() => setActiveTab('security')}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'security' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
                data-testid="security-tab-btn"
              >
                <ShieldCheck className="w-4 h-4" /> My Security
              </button>
              <button
                onClick={() => setActiveTab('media-health')}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'media-health' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <span className="flex items-center gap-3">
                  <Shield className="w-4 h-4" /> Media Health
                </span>
                {mediaHealth?.summary?.broken > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{mediaHealth.summary.broken}</span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('trash')}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                  activeTab === 'trash' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                }`}
              >
                <span className="flex items-center gap-3">
                  <Trash2 className="w-4 h-4" /> Trash
                </span>
                {trashItems.length > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{trashItems.length}</span>
                )}
              </button>
              {/* SEO Manager - Admin Only */}
              {isAdmin() && (
                <button
                  onClick={() => setActiveTab('seo')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeTab === 'seo' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                  }`}
                  data-testid="seo-manager-tab-btn"
                >
                  <BarChart2 className="w-4 h-4" /> SEO Manager
                </button>
              )}
              {/* Data Migration - Admin Only */}
              {isAdmin() && (
                <button
                  onClick={() => setActiveTab('data-migration')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                    activeTab === 'data-migration' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800'
                  }`}
                  data-testid="data-migration-tab-btn"
                >
                  <Database className="w-4 h-4" /> Data Migration
                </button>
              )}
            </div>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 bg-gray-100 p-6 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="max-w-6xl mx-auto">
              {/* Render active tab content */}
              {activeTab === 'dashboard' && <DashboardTab />}
              {activeTab === 'analytics' && <AnalyticsDashboard />}
              {activeTab === 'homepage' && <HomepageTab />}
              {activeTab === 'images' && <ImageLibraryManager />}
              {activeTab === 'news' && <NewsManagementTab />}
              {activeTab === 'users' && isAdmin() && <UserManagement />}
              {activeTab === 'services' && <ContentTab type="services" items={services} titleKey="title" subtitleKey="description" imageKey="image" addLabel="Service" />}
              {activeTab === 'services-overview' && <ServicesOverviewTab />}
              {activeTab === 'sectors' && <ContentTab type="sectors" items={sectors} titleKey="title" subtitleKey="description" imageKey="image" addLabel="Sector" />}
              {activeTab === 'projects' && <ContentTab type="projects" items={projects} titleKey="name" subtitleKey="client" imageKey="image" addLabel="Project" />}
              {activeTab === 'team' && <ContentTab type="team" items={teamMembers} titleKey="name" subtitleKey="position" imageKey="image" addLabel="Team Member" />}
              {activeTab === 'jobs' && <ContentTab type="jobs" items={jobListings} titleKey="title" subtitleKey="type" addLabel="Job" />}
              {activeTab === 'company' && <CompanyInfoTab />}
              {activeTab === 'contact' && <ContactSettingsTab />}
              {activeTab === 'accreditations' && <AccreditationsTab />}
              {activeTab === 'career-progression' && <CareerProgressionTab />}
              {activeTab === 'pages' && <PagesTab />}
              {activeTab === 'about-page' && <AboutPageTab />}
              {activeTab === 'media-health' && <MediaHealthTab />}
              {activeTab === 'trash' && <TrashTab />}
              {activeTab === 'settings' && <SiteSettingsTab />}
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Account Security</h2>
                    <p className="text-gray-600">Manage your account security settings</p>
                  </div>
                  <TwoFactorSetup />
                </div>
              )}
              {activeTab === 'data-migration' && isAdmin() && <DataMigration />}
              {activeTab === 'seo' && isAdmin() && <SEOManager />}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Admin;
